import { jsPDF } from 'jspdf';
import { BRAND } from '../config/brand';
import { getSignedUrl } from './storage';
import { distanceMeters } from './geo';
import { TICK_LISTS, flattenItems } from '../data/tickListTemplates';
import { RIDDOR_LABELS } from './riddor';
import { SUBBIE_INSURANCE, SUBBIE_COMPETENCE, INDUCTION_TOPICS, INDUCTION_DECLARATION } from './subbieInduction';
import type {
  Site, SignIn, TickListRun, FormRow, FormAttendeeRow,
} from './db';

// ============================================================
// SHARED LAYOUT HELPERS
// ============================================================

const MARGIN = 15;
const PAGE_W = 210;
const PAGE_H = 297;
const CONTENT_W = PAGE_W - MARGIN * 2;

const COLOUR_INK = '#0F172A';
const COLOUR_INK_SOFT = '#475569';
const COLOUR_BRAND = '#1E293B';
const COLOUR_BRAND_DEEP = '#0F172A';
const COLOUR_BORDER = '#E5E7E9';
const COLOUR_SUCCESS = '#16A34A';
const COLOUR_DANGER = '#B0413E';
const COLOUR_WARN = '#BF8E3D';

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function fmtDateLong(iso?: string): string {
  const d = iso ? new Date(iso) : new Date();
  return d.toLocaleDateString('en-GB', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  });
}

async function fetchSignedDataUrl(bucket: string, path: string): Promise<string | null> {
  try {
    const url = await getSignedUrl(bucket, path, 3600);
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

interface HeaderOpts {
  orgName: string;
  logoDataUrl: string | null;
  title: string;
  subtitle: string;
  /** Unused since the Sep 2026 refresh — page numbers are stamped by
   *  finishPdf() once the whole document is laid out. Kept so existing
   *  call sites don't need touching. */
  pageNum?: number;
  totalPages?: number;
  riddor?: boolean;
  /** Small caption under the org name, top right (e.g. "Accident report"). */
  docType?: string;
}

/**
 * Shared document header (Sep 2026 refresh, matches the RAMS redesign):
 *  - logo in its own band at the top-left with clear space below it
 *  - org name + document type top-right
 *  - big title, grey subtitle, strong rule
 * Continuation pages (title containing "(cont") get a compact version.
 * Returns the y-position where body content should start.
 */
function drawHeader(doc: jsPDF, h: HeaderOpts): number {
  const cont = /\(cont/i.test(h.title);
  const title = h.title.replace(/\s*\(cont\.?\)\s*$/i, ' (continued)');
  const top = 12;
  let logoBottom = top;
  if (h.logoDataUrl) {
    try {
      const props = doc.getImageProperties(h.logoDataUrl);
      const aspect = props.width / props.height;
      const maxW = cont ? 26 : 34, maxH = cont ? 12 : 16;
      let w = maxW, ih = maxW / aspect;
      if (ih > maxH) { ih = maxH; w = maxH * aspect; }
      doc.addImage(h.logoDataUrl, 'AUTO', MARGIN, top, w, ih);
      logoBottom = top + ih;
    } catch { /* fall through to text-only header */ }
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(COLOUR_INK);
  doc.text(h.orgName || '', PAGE_W - MARGIN, top + 4, { align: 'right' });
  if (h.docType) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(COLOUR_INK_SOFT);
    doc.text(h.docType, PAGE_W - MARGIN, top + 8.5, { align: 'right' });
  }

  let y = Math.max(logoBottom, top + 10) + (cont ? 8 : 11);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(cont ? 14 : 20);
  doc.setTextColor(COLOUR_INK);
  const titleLines = wrapLines(doc, title, CONTENT_W);
  doc.text(titleLines, MARGIN, y);
  y += (titleLines.length - 1) * (cont ? 6 : 8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(cont ? 9 : 11);
  doc.setTextColor(COLOUR_INK_SOFT);
  y += cont ? 5 : 6.5;
  doc.text(h.subtitle, MARGIN, y);
  y += 4;

  if (h.riddor && !cont) {
    y += 2;
    doc.setFillColor(COLOUR_DANGER);
    doc.roundedRect(MARGIN, y, 44, 7, 1.5, 1.5, 'F');
    doc.setTextColor('#FFFFFF');
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.text('RIDDOR-REPORTABLE', MARGIN + 22, y + 4.7, { align: 'center' });
    y += 11;
  }

  doc.setDrawColor(COLOUR_BRAND_DEEP);
  doc.setLineWidth(cont ? 0.3 : 0.8);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  return y + (cont ? 7 : 9);
}

/** Kept as a no-op so old call sites compile — footers (brand, timestamp,
 *  "Page X of Y") are stamped on every page by finishPdf(). */
function drawFooter(_doc: jsPDF) { /* stamped in finishPdf */ }

/** Stamp the footer on every page, then return the PDF blob. */
function finishPdf(doc: jsPDF): Blob {
  const total = doc.getNumberOfPages();
  const ts = new Date().toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setDrawColor(COLOUR_BORDER);
    doc.setLineWidth(0.25);
    doc.line(MARGIN, PAGE_H - 13, PAGE_W - MARGIN, PAGE_H - 13);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLOUR_INK_SOFT);
    doc.text(`Generated by ${BRAND.name}  ·  ${ts}`, MARGIN, PAGE_H - 8);
    doc.setFont('helvetica', 'bold');
    doc.text(`Page ${i} of ${total}`, PAGE_W - MARGIN, PAGE_H - 8, { align: 'right' });
  }
  return doc.output('blob');
}

/** Section heading: small accent bar, bold caps title, hairline rule.
 *  Returns the y where the section body should start. */
function drawSectionTitle(doc: jsPDF, title: string, y: number, x = MARGIN, w = CONTENT_W): number {
  doc.setFillColor(COLOUR_BRAND_DEEP);
  doc.rect(x, y - 3.6, 1.2, 4.6, 'F');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLOUR_BRAND_DEEP);
  doc.text(title.toUpperCase(), x + 3.5, y);
  doc.setDrawColor(COLOUR_BORDER);
  doc.setLineWidth(0.25);
  doc.line(x, y + 2.5, x + w, y + 2.5);
  return y + 8;
}

/** Light grey panel with a grid of LABEL / value pairs. Returns next y. */
function drawInfoPanel(doc: jsPDF, y: number, cells: Array<[string, string]>, cols = 3): number {
  const pad = 5, gap = 5;
  const colW = (CONTENT_W - pad * 2 - gap * (cols - 1)) / cols;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const rows = Math.ceil(cells.length / cols);
  const rowH: number[] = [];
  for (let r = 0; r < rows; r++) {
    let n = 1;
    for (let c = 0; c < cols; c++) {
      const cell = cells[r * cols + c];
      if (cell) n = Math.max(n, wrapLines(doc, cell[1] || '—', colW).length);
    }
    rowH.push(4 + n * 4.3 + 4);
  }
  const panelH = pad * 2 + rowH.reduce((a, b) => a + b, 0) - 4;
  doc.setFillColor('#F8FAFC');
  doc.setDrawColor(COLOUR_BORDER);
  doc.setLineWidth(0.25);
  doc.roundedRect(MARGIN, y, CONTENT_W, panelH, 2, 2, 'FD');
  let ry = y + pad + 3;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const cell = cells[r * cols + c];
      if (!cell) continue;
      const [label, value] = cell;
      const x = MARGIN + pad + c * (colW + gap);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(COLOUR_INK_SOFT);
      doc.text(label.toUpperCase(), x, ry);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(value ? COLOUR_INK : COLOUR_INK_SOFT);
      doc.text(wrapLines(doc, value || '—', colW), x, ry + 4.5);
    }
    ry += rowH[r];
  }
  return y + panelH + 8;
}

/** Signature box with a name/date line underneath the signature. */
function drawSignatureBlock(
  doc: jsPDF, y: number, sigDataUrl: string | null, name: string, date: string,
  x0 = MARGIN, width = CONTENT_W / 2 + 10,
): number {
  const boxW = width;
  const MARGIN = x0; // shadow: draw relative to the supplied x
  doc.setDrawColor(COLOUR_BORDER);
  doc.setLineWidth(0.3);
  doc.roundedRect(MARGIN, y, boxW, 24, 1.5, 1.5, 'S');
  if (sigDataUrl) {
    try { doc.addImage(sigDataUrl, 'AUTO', MARGIN + 2, y + 1.5, boxW - 4, 15); } catch { /* noop */ }
  }
  doc.line(MARGIN + 3, y + 18, MARGIN + boxW - 3, y + 18);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLOUR_INK);
  doc.text(name || '', MARGIN + 3, y + 22);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(COLOUR_INK_SOFT);
  doc.text(date || '', MARGIN + boxW - 3, y + 22, { align: 'right' });
  return y + 30;
}

/** Wrap text and return the array of wrapped lines. */
function wrapLines(doc: jsPDF, text: string, maxW: number): string[] {
  return doc.splitTextToSize(text, maxW) as string[];
}

// ============================================================
// REUSABLE PRESENTATION HELPERS
// ============================================================
// Small drawing primitives shared across every PDF generator below.
// Goals: consistent visual language, less duplication, no emojis (the
// brand brief is explicit — clean professional documents).
// ============================================================

/**
 * Filled rounded pill for severity labels (HIGH / MED / LOW). Returns
 * the width consumed so the caller can lay out adjacent text. Brand
 * colours via COLOUR_DANGER / COLOUR_WARN / COLOUR_SUCCESS.
 */
function drawSeverityBadge(
  doc: jsPDF,
  x: number,
  y: number,
  level: 'High' | 'Med' | 'Low',
): { width: number; height: number } {
  const colour = level === 'High' ? COLOUR_DANGER
               : level === 'Med'  ? COLOUR_WARN
               :                    COLOUR_SUCCESS;
  const w = 16;
  const h = 5.5;
  doc.setFillColor(colour);
  doc.setDrawColor(colour);
  doc.roundedRect(x, y, w, h, 1.2, 1.2, 'F');
  doc.setTextColor('#FFFFFF');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(level.toUpperCase(), x + w / 2, y + h - 1.6, { align: 'center' });
  return { width: w, height: h };
}

/**
 * Generic status pill — small rounded badge with a coloured tint. Used
 * for "ON SITE" / "SIGNED OUT" on the sign-in log, "ISSUED" / "CLOSED"
 * on permits, etc. Light fill + matching border + dark text rather than
 * heavy full colour, so the pill reads as informative not alarming.
 */
function drawStatusPill(
  doc: jsPDF,
  x: number,
  y: number,
  text: string,
  kind: 'success' | 'muted' | 'warn' | 'danger',
): number {
  const palette = {
    success: { fill: '#EAF3EE', text: COLOUR_SUCCESS },
    muted:   { fill: '#EEEEEE', text: COLOUR_INK_SOFT },
    warn:    { fill: '#FFF8E5', text: COLOUR_WARN },
    danger:  { fill: '#FAE9E8', text: COLOUR_DANGER },
  }[kind];
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  const padX = 3;
  const w = doc.getTextWidth(text.toUpperCase()) + padX * 2;
  const h = 5;
  doc.setFillColor(palette.fill);
  doc.setDrawColor(palette.text);
  doc.setLineWidth(0.2);
  doc.roundedRect(x, y, w, h, 1, 1, 'FD');
  doc.setTextColor(palette.text);
  doc.text(text.toUpperCase(), x + w / 2, y + h - 1.5, { align: 'center' });
  return w;
}

/**
 * Small filled circle with a number inside, for numbered method/sequence
 * steps. Quieter visual than "1." in body text, still scannable.
 */
function drawNumberCircle(
  doc: jsPDF,
  x: number,
  y: number,
  n: number,
): { width: number; height: number } {
  const r = 2.6;
  doc.setFillColor(COLOUR_BRAND_DEEP);
  doc.circle(x + r, y - r + 0.5, r, 'F');
  doc.setTextColor('#FFFFFF');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text(String(n), x + r, y - r + 1.8, { align: 'center' });
  return { width: r * 2, height: r * 2 };
}

/**
 * Printed-review checkbox (bigger + more prominent than the inline
 * drawCheckRow version, intended for pre-use review sections an HSE
 * inspector will print and tick by pen).
 */
function drawCheckbox(
  doc: jsPDF,
  x: number,
  y: number,
  checked: boolean,
  size = 5,
): void {
  if (checked) {
    doc.setFillColor(COLOUR_BRAND_DEEP);
    doc.setDrawColor(COLOUR_BRAND_DEEP);
    doc.roundedRect(x, y - size + 1, size, size, 0.6, 0.6, 'FD');
    doc.setDrawColor('#FFFFFF');
    doc.setLineWidth(0.8);
    doc.line(x + 1.0, y - 1.5, x + 2.1, y - 0.4);
    doc.line(x + 2.1, y - 0.4, x + 4.0, y - 3.0);
    doc.setLineWidth(0.3);
  } else {
    doc.setDrawColor(COLOUR_INK);
    doc.setLineWidth(0.4);
    doc.rect(x, y - size + 1, size, size, 'S');
  }
}

/**
 * Lightly-tinted alternating background for zebra-striped tables. Pass
 * the row index to decide which rows get the tint. Subtle off-white so
 * it improves scanning without looking like a spreadsheet.
 */
function drawZebraRow(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  index: number,
): void {
  if (index % 2 === 1) {
    doc.setFillColor('#FAFAFA');
    doc.rect(x, y, w, h, 'F');
  }
}

/** Draw a labelled value pair (label gray, value black). Returns next y. */
function drawField(doc: jsPDF, x: number, y: number, label: string, value: string, valueWidth: number): number {
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLOUR_INK_SOFT);
  doc.text(label.toUpperCase(), x, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(COLOUR_INK);
  const lines = wrapLines(doc, value || '—', valueWidth);
  doc.text(lines, x, y + 4);
  return y + 4 + lines.length * 4;
}

function drawCheckRow(doc: jsPDF, x: number, y: number, ticked: boolean, label: string, maxW: number): number {
  drawCheckbox(doc, x, y + 1, ticked, 4.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(COLOUR_INK);
  const lines = wrapLines(doc, label, maxW);
  doc.text(lines, x + 8, y);
  return y + lines.length * 4.3 + 3;
}

// ============================================================
// SIGN-IN LOG PDF
// ============================================================

interface SignInLogOpts {
  orgName: string;
  orgLogoPath: string | null;
  site: Site;
  signIns: SignIn[];
}

const SIGN_ROW_HEIGHT = 32;

export async function generateSignInLogPdf(opts: SignInLogOpts): Promise<Blob> {
  const sigImages: Record<string, string | null> = {};
  await Promise.all(
    opts.signIns.map(async (p) => {
      if (!p.signature_in_url) return;
      sigImages[p.id] = await fetchSignedDataUrl('signatures', p.signature_in_url);
    })
  );
  const logoDataUrl = opts.orgLogoPath ? await fetchSignedDataUrl('org-logos', opts.orgLogoPath) : null;
  const dateLabel = fmtDateLong();

  // Pre-compute the summary metrics that go in the header. Site managers
  // and HSE inspectors want the at-a-glance numbers ("how many on site
  // right now") before they read the per-person rows below.
  const onSite = opts.signIns.filter((p) => !p.time_out).length;
  const signedOut = opts.signIns.filter((p) => !!p.time_out).length;
  const visitors = opts.signIns.filter((p) => (p as { is_visitor?: boolean }).is_visitor === true).length;

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  // Reserve more vertical space at the top now that we render a summary
  // strip under the title (was 36mm pre-summary). Each row is the same
  // height as before for body density.
  const headerOffset = 78;
  const rowsPerPage = Math.floor((PAGE_H - MARGIN - headerOffset - 12) / SIGN_ROW_HEIGHT);
  const totalPages = Math.max(1, Math.ceil(opts.signIns.length / rowsPerPage));

  /** Title + subtitle + summary strip in one consistent header block. */
  function drawSignInHeader(pageNum: number) {
    const baseY = drawHeader(doc, {
      orgName: opts.orgName, logoDataUrl,
      title: 'Site sign-in log',
      docType: 'Sign-in register',
      subtitle: `${opts.site.name} · ${dateLabel}`,
      pageNum, totalPages,
    });
    // Summary strip — 3 small stat blocks across the top of the body.
    const stripY = baseY;
    const blockW = (CONTENT_W - 8) / 3;
    const stats: Array<{ label: string; value: number; tint: 'success' | 'muted' | 'warn' }> = [
      { label: 'On site now',      value: onSite,    tint: 'success' },
      { label: 'Signed out today', value: signedOut, tint: 'muted'   },
      { label: 'Visitors',         value: visitors,  tint: 'warn'    },
    ];
    stats.forEach((stat, i) => {
      const x = MARGIN + i * (blockW + 4);
      const tintFill = stat.tint === 'success' ? '#EAF3EE'
                     : stat.tint === 'warn'    ? '#FFF8E5'
                     :                           '#F5F5F5';
      doc.setFillColor(tintFill);
      doc.setDrawColor(COLOUR_BORDER);
      doc.setLineWidth(0.2);
      doc.roundedRect(x, stripY, blockW, 14, 1.5, 1.5, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(COLOUR_INK);
      doc.text(String(stat.value), x + 4, stripY + 9);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(COLOUR_INK_SOFT);
      doc.text(stat.label.toUpperCase(), x + 4, stripY + 12.5);
    });
    return stripY + 14 + 6;
  }

  if (opts.signIns.length === 0) {
    drawSignInHeader(1);
    doc.setFontSize(11);
    doc.setTextColor(COLOUR_INK_SOFT);
    doc.text('No sign-ins on this date.', MARGIN, MARGIN + headerOffset + 6);
    return finishPdf(doc);
  }

  for (let pageIdx = 0; pageIdx < totalPages; pageIdx++) {
    if (pageIdx > 0) doc.addPage();
    let y = drawSignInHeader(pageIdx + 1);

    const slice = opts.signIns.slice(pageIdx * rowsPerPage, (pageIdx + 1) * rowsPerPage);
    slice.forEach((p, idx) => {
      const rowH = SIGN_ROW_HEIGHT - 2;
      // Zebra stripe — subtle off-white on every second row for scanning.
      drawZebraRow(doc, MARGIN, y, CONTENT_W, rowH, idx);
      // Soft outer border on every row.
      doc.setDrawColor(COLOUR_BORDER);
      doc.setLineWidth(0.2);
      doc.roundedRect(MARGIN, y, CONTENT_W, rowH, 1.5, 1.5, 'S');

      const leftX = MARGIN + 4;
      let textY = y + 6;

      // Name (left) + status pill (right of name).
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(COLOUR_INK);
      doc.text(p.full_name, leftX, textY);
      const nameW = doc.getTextWidth(p.full_name);
      const isOnSite = !p.time_out;
      const pillW = drawStatusPill(
        doc,
        leftX + nameW + 4,
        textY - 4,
        isOnSite ? 'On site' : 'Signed out',
        isOnSite ? 'success' : 'muted',
      );
      void pillW;

      // Time range, right-aligned on the same line as the name.
      const timeStr = p.time_out ? `${fmtTime(p.time_in)} - ${fmtTime(p.time_out)}` : `Since ${fmtTime(p.time_in)}`;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(COLOUR_BRAND_DEEP);
      doc.text(timeStr, MARGIN + CONTENT_W - 56, textY, { align: 'right' });

      textY += 5;
      doc.setFontSize(9);
      doc.setTextColor(COLOUR_INK_SOFT);
      doc.text([p.company, p.trade_role].filter(Boolean).join(' · '), leftX, textY);

      const detailParts = [
        p.mobile ? `Mobile ${p.mobile}` : '',
        p.vehicle_reg ? `Vehicle ${p.vehicle_reg}` : '',
      ].filter(Boolean);
      if (detailParts.length > 0) { textY += 5; doc.text(detailParts.join(' · '), leftX, textY); }

      // GPS distance demoted to a small secondary caption — site managers
      // care more about presence than precise distance, so this gets a
      // small grey line rather than its own row.
      if (p.in_lat != null && p.in_lng != null && opts.site.centre_lat != null && opts.site.centre_lng != null) {
        const dist = Math.round(distanceMeters(p.in_lat, p.in_lng, opts.site.centre_lat, opts.site.centre_lng));
        const radius = opts.site.geofence_radius_m ?? 200;
        textY += 4;
        doc.setFontSize(8);
        if (dist <= radius) {
          doc.setTextColor(COLOUR_INK_SOFT);
          doc.text(`${dist}m from site centre`, leftX, textY);
        } else {
          doc.setTextColor(COLOUR_DANGER);
          doc.text(`Off-site: ${dist}m from centre`, leftX, textY);
        }
      }

      const sigBoxX = MARGIN + CONTENT_W - 50 - 4;
      const sigBoxY = y + 8;
      const sigBoxW = 50;
      const sigBoxH = SIGN_ROW_HEIGHT - 14;
      doc.setDrawColor(COLOUR_BRAND);
      doc.setLineWidth(0.15);
      doc.roundedRect(sigBoxX, sigBoxY, sigBoxW, sigBoxH, 1, 1, 'S');
      const sig = sigImages[p.id];
      if (sig) {
        try { doc.addImage(sig, 'AUTO', sigBoxX + 1, sigBoxY + 1, sigBoxW - 2, sigBoxH - 2); }
        catch { /* noop */ }
      } else {
        doc.setFontSize(7);
        doc.setTextColor(COLOUR_BRAND);
        doc.text('No signature', sigBoxX + sigBoxW / 2, sigBoxY + sigBoxH / 2 + 1, { align: 'center' });
      }

      y += SIGN_ROW_HEIGHT;
    });
  }

  return finishPdf(doc);
}

// ============================================================
// TICK LIST RUN PDF
// ============================================================

interface TickRunOpts {
  orgName: string;
  orgLogoPath: string | null;
  site: Site;
  run: TickListRun;
}

export async function generateTickListRunPdf(opts: TickRunOpts): Promise<Blob> {
  const tpl = TICK_LISTS[opts.run.kind];
  if (!tpl) throw new Error(`Unknown tick list kind: ${opts.run.kind}`);

  const flat = flattenItems(tpl);
  // Pre-fetch all photos referenced in responses
  const photoCache: Record<string, string | null> = {};
  for (const r of Object.values(opts.run.responses)) {
    if (!r?.photo_paths) continue;
    for (const path of r.photo_paths) {
      photoCache[path] = await fetchSignedDataUrl('accident-photos', path);
    }
  }
  // Try to find a signature for this run
  const sigPath = `${opts.site.org_id}/tick-${opts.run.id}/in.png`;
  const sigDataUrl = await fetchSignedDataUrl('signatures', sigPath);
  const logoDataUrl = opts.orgLogoPath ? await fetchSignedDataUrl('org-logos', opts.orgLogoPath) : null;

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  const responses = Object.values(opts.run.responses);
  const passCount = responses.filter((r) => r?.state === 'pass').length;
  const failCount = responses.filter((r) => r?.state === 'fail').length;
  const naCount = responses.filter((r) => r?.state === 'na').length;
  const totalCount = flat.length;

  let pageNum = 1;
  let y = drawHeader(doc, {
    orgName: opts.orgName, logoDataUrl,
    title: tpl.title,
    docType: 'Site check',
    subtitle: `${opts.site.name} · ${fmtDateLong(opts.run.performed_at)}`,
    pageNum, totalPages: 1, // we'll patch totalPages after
  });

  // Summary panel
  y = drawInfoPanel(doc, y, [
    ['Checked by', opts.run.performed_by_name ?? 'Unknown'],
    ['Items OK', `${passCount} of ${totalCount}`],
    ['Issues', `${failCount}${naCount > 0 ? `  (${naCount} N/A)` : ''}`],
  ], 3);

  if (opts.run.notes) {
    y = drawField(doc, MARGIN, y, 'Notes', opts.run.notes, CONTENT_W);
    y += 2;
  }

  function ensureSpace(needed: number) {
    if (y + needed > PAGE_H - 18) {
      drawFooter(doc);
      doc.addPage();
      pageNum++;
      y = drawHeader(doc, {
        orgName: opts.orgName, logoDataUrl,
        title: `${tpl!.title} (cont.)`,
        docType: 'Site check',
        subtitle: `${opts.site.name} · ${fmtDateLong(opts.run.performed_at)}`,
        pageNum, totalPages: pageNum,  // approximate
      });
    }
  }

  // Items per section
  let itemNumber = 0;
  for (let si = 0; si < tpl.sections.length; si++) {
    const sec = tpl.sections[si];
    if (sec.name) {
      ensureSpace(22);
      y = drawSectionTitle(doc, sec.name, y + 2);
    }
    for (let ii = 0; ii < sec.items.length; ii++) {
      itemNumber++;
      const item = sec.items[ii];
      const key = `s${si}_i${ii}`;
      const r = opts.run.responses[key];
      const text = item.text;
      const desc = item.desc;
      const photos = (r?.photo_paths ?? []).map((p) => photoCache[p]).filter(Boolean) as string[];

      const wrapped = wrapLines(doc, `${itemNumber}. ${text}`, CONTENT_W - 24);
      const descLines = desc ? wrapLines(doc, desc, CONTENT_W - 24) : [];
      const noteLines = r?.notes ? wrapLines(doc, `Notes: ${r.notes}`, CONTENT_W - 6) : [];
      const photoH = photos.length > 0 ? 22 : 0;
      const rowH = Math.max(8, wrapped.length * 4) + descLines.length * 3.5 + noteLines.length * 4 + photoH + 4;

      ensureSpace(rowH);

      // Item text
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(COLOUR_INK);
      doc.text(wrapped, MARGIN, y + 4);

      // State chip on right
      const stateLabel = r?.state === 'pass' ? 'OK' : r?.state === 'fail' ? 'ISSUE' : r?.state === 'na' ? 'N/A' : '—';
      const stateColour = r?.state === 'pass' ? COLOUR_SUCCESS : r?.state === 'fail' ? COLOUR_DANGER : r?.state === 'na' ? COLOUR_INK_SOFT : '#B8C0C5';
      doc.setFillColor(stateColour);
      doc.roundedRect(PAGE_W - MARGIN - 16, y, 16, 5, 1, 1, 'F');
      doc.setTextColor('#FFFFFF');
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text(stateLabel, PAGE_W - MARGIN - 8, y + 3.5, { align: 'center' });

      let lineY = y + 4 + wrapped.length * 4;
      if (descLines.length > 0) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(COLOUR_INK_SOFT);
        doc.text(descLines, MARGIN + 4, lineY);
        lineY += descLines.length * 3.5;
      }
      if (noteLines.length > 0) {
        doc.setFontSize(9);
        doc.setTextColor(COLOUR_DANGER);
        doc.text(noteLines, MARGIN + 4, lineY + 2);
        lineY += noteLines.length * 4;
      }
      if (photos.length > 0) {
        const thumbW = 22, thumbH = 22, gap = 3;
        let px = MARGIN + 4;
        const py = lineY + 2;
        for (const p of photos.slice(0, 4)) {
          try { doc.addImage(p, 'AUTO', px, py, thumbW, thumbH); } catch { /* noop */ }
          px += thumbW + gap;
        }
        lineY = py + thumbH;
      }

      y = lineY + 3;
      doc.setDrawColor(COLOUR_BORDER);
      doc.setLineWidth(0.15);
      doc.line(MARGIN, y, PAGE_W - MARGIN, y);
      y += 2;
    }
  }

  if (tpl.tail) {
    ensureSpace(14);
    doc.setFillColor('#FAF1E0');
    doc.roundedRect(MARGIN, y, CONTENT_W, 10, 1.5, 1.5, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLOUR_WARN);
    doc.text(tpl.tail, MARGIN + 4, y + 6.5);
    y += 14;
  }

  if (sigDataUrl) {
    ensureSpace(44);
    y = drawSectionTitle(doc, 'Signed off', y + 4);
    y = drawSignatureBlock(doc, y, sigDataUrl, opts.run.performed_by_name ?? '', fmtDateLong(opts.run.performed_at));
  }

  return finishPdf(doc);
}

// ============================================================
// TOOLBOX TALK PDF
// ============================================================

interface FormPdfOpts {
  orgName: string;
  orgLogoPath: string | null;
  site: Site | null;
  form: FormRow;
}

interface ToolboxTalkOpts extends FormPdfOpts {
  attendees: FormAttendeeRow[];
}

export async function generateToolboxTalkPdf(opts: ToolboxTalkOpts): Promise<Blob> {
  const data = opts.form.data as Record<string, string>;
  const logoDataUrl = opts.orgLogoPath ? await fetchSignedDataUrl('org-logos', opts.orgLogoPath) : null;
  const presenterSig = opts.form.signature_url ? await fetchSignedDataUrl('signatures', opts.form.signature_url) : null;

  const attendeeSigs: Record<string, string | null> = {};
  await Promise.all(
    opts.attendees.map(async (a) => {
      if (!a.signature_url) return;
      attendeeSigs[a.id] = await fetchSignedDataUrl('signatures', a.signature_url);
    })
  );

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = drawHeader(doc, {
    orgName: opts.orgName, logoDataUrl,
    title: 'Toolbox talk',
    docType: 'Toolbox talk record',
    subtitle: `${opts.site?.name ?? 'Site'} · ${fmtDateLong(opts.form.performed_at)}`,
    pageNum: 1, totalPages: 1,
  });
  const cont = () => drawHeader(doc, {
    orgName: opts.orgName, logoDataUrl, docType: 'Toolbox talk record',
    title: 'Toolbox talk (cont.)',
    subtitle: `${opts.site?.name ?? 'Site'} · ${fmtDateLong(opts.form.performed_at)}`,
  });
  function ensure(n: number) { if (y + n > PAGE_H - 17) { doc.addPage(); y = cont(); } }

  /** Labelled long-text block: small caps label then wrapped paragraph. */
  function textBlock(label: string, value: string) {
    if (!value) return;
    doc.setFontSize(10);
    const lines = wrapLines(doc, value, CONTENT_W);
    ensure(lines.length * 4.4 + 10);
    doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(COLOUR_INK_SOFT);
    doc.text(label.toUpperCase(), MARGIN, y);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(COLOUR_INK);
    doc.text(lines, MARGIN, y + 5);
    y += 5 + lines.length * 4.4 + 4;
  }

  y = drawInfoPanel(doc, y, [
    ['Topic', data.topic ?? ''],
    ['Delivered by', opts.form.performed_by_name ?? 'Unknown'],
    ['Duration', data.mins ? `${data.mins} mins` : ''],
    ['Attendees', String(opts.attendees.length)],
  ], 2);

  if (data.why || data.points || data.actions) {
    ensure(20);
    y = drawSectionTitle(doc, 'Talk content', y);
    textBlock('Why this topic', data.why ?? '');
    textBlock('Key points', data.points ?? '');
    textBlock('Actions agreed', data.actions ?? '');
    y += 2;
  }

  // Attendees grid (2 per row)
  ensure(40);
  y = drawSectionTitle(doc, `Attendees (${opts.attendees.length})`, y);

  const colW = (CONTENT_W - 6) / 2;
  const rowH = 26;
  let col = 0;
  let rowY = y;
  for (const a of opts.attendees) {
    const x = MARGIN + col * (colW + 6);
    if (rowY + rowH > PAGE_H - 20) {
      doc.addPage();
      rowY = cont();
    }
    doc.setDrawColor(COLOUR_BORDER);
    doc.setLineWidth(0.2);
    doc.roundedRect(x, rowY, colW, rowH, 1.5, 1.5, 'S');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(COLOUR_INK);
    doc.text(a.full_name, x + 3, rowY + 5);
    if (a.company) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(COLOUR_INK_SOFT);
      doc.text(a.company, x + 3, rowY + 10);
    }
    const sig = attendeeSigs[a.id];
    if (sig) {
      try { doc.addImage(sig, 'AUTO', x + 3, rowY + 12, colW - 6, rowH - 14); } catch { /* noop */ }
    }
    col++;
    if (col === 2) { col = 0; rowY += rowH + 4; }
  }
  if (col === 1) rowY += rowH + 4;
  y = rowY;

  ensure(44);
  y = drawSectionTitle(doc, 'Presenter sign-off', y + 4);
  y = drawSignatureBlock(doc, y, presenterSig, opts.form.performed_by_name ?? '', fmtDateLong(opts.form.performed_at));

  return finishPdf(doc);
}

// ============================================================
// ACCIDENT REPORT PDF
// ============================================================

export async function generateAccidentPdf(opts: FormPdfOpts): Promise<Blob> {
  // Compliance pass Sep 2026 — covers the HSE accident book (BI 510)
  // fields, RIDDOR reporting status, actions with owner/date, the
  // investigation timeline and a manager review / close-out block.
  const data = opts.form.data as Record<string, unknown>;
  const str = (k: string) => (data[k] == null ? '' : String(data[k]));
  const fmtD = (v: string) => {
    if (!v) return '';
    const d = new Date(v);
    return isNaN(d.getTime()) ? v : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const logoDataUrl = opts.orgLogoPath ? await fetchSignedDataUrl('org-logos', opts.orgLogoPath) : null;
  const sigDataUrl = opts.form.signature_url ? await fetchSignedDataUrl('signatures', opts.form.signature_url) : null;
  const photoPaths = (data.photo_paths as string[] | undefined) ?? [];
  const photos: string[] = [];
  for (const p of photoPaths) {
    const url = await fetchSignedDataUrl('accident-photos', p);
    if (url) photos.push(url);
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const subtitle = `${opts.site?.name ?? 'Site'} · ${fmtDateLong(opts.form.performed_at)}`;
  let y = drawHeader(doc, {
    orgName: opts.orgName, logoDataUrl,
    title: 'Accident / near-miss report',
    docType: 'Accident book record',
    subtitle,
    riddor: opts.form.is_riddor ?? false,
  });
  const cont = () => drawHeader(doc, {
    orgName: opts.orgName, logoDataUrl, docType: 'Accident book record',
    title: 'Accident / near-miss report (cont.)', subtitle,
  });
  function ensure(n: number) { if (y + n > PAGE_H - 17) { doc.addPage(); y = cont(); } }
  function section(t: string, keep = 16) { ensure(14 + keep); y = drawSectionTitle(doc, t, y + 2); }
  function textBlock(label: string, value: string) {
    if (!value) return;
    doc.setFontSize(10);
    const lines = wrapLines(doc, value, CONTENT_W);
    ensure(lines.length * 4.4 + 10);
    doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(COLOUR_INK_SOFT);
    doc.text(label.toUpperCase(), MARGIN, y);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(COLOUR_INK);
    doc.text(lines, MARGIN, y + 5);
    y += 5 + lines.length * 4.4 + 4;
  }
  function panel(cells: Array<[string, string]>, cols = 3) {
    ensure(30);
    y = drawInfoPanel(doc, y, cells, cols);
  }
  function callout(title: string, body: string, kind: 'danger' | 'success' | 'muted') {
    const fill = kind === 'danger' ? '#FDF2F2' : kind === 'success' ? '#EAF3EE' : '#F8FAFC';
    const bar = kind === 'danger' ? COLOUR_DANGER : kind === 'success' ? COLOUR_SUCCESS : COLOUR_INK_SOFT;
    doc.setFontSize(9.5);
    const lines = wrapLines(doc, body, CONTENT_W - 12);
    const h = 10 + lines.length * 4.2;
    ensure(h + 4);
    doc.setFillColor(fill); doc.setDrawColor(COLOUR_BORDER); doc.setLineWidth(0.25);
    doc.roundedRect(MARGIN, y, CONTENT_W, h, 1.8, 1.8, 'FD');
    doc.setFillColor(bar); doc.rect(MARGIN, y, 1.6, h, 'F');
    doc.setFontSize(9.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(bar);
    doc.text(title, MARGIN + 6, y + 5.5);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(COLOUR_INK);
    doc.text(lines, MARGIN + 6, y + 10.5);
    y += h + 5;
  }

  // ===== Summary =====
  panel([
    ['Type', str('type')],
    ['Date · time', `${fmtD(str('date'))} ${str('time')}`.trim()],
    ['Location on site', str('location')],
  ], 3);

  // Data-protection note up front (UK GDPR) — short, not a disclaimer.
  doc.setFontSize(7.5); doc.setFont('helvetica', 'italic'); doc.setTextColor(COLOUR_INK_SOFT);
  doc.text('Confidential — contains personal and health information. Keep secure; share only with people who need it (UK GDPR). Retain for at least 3 years.', MARGIN, y - 3);
  y += 5;

  // ===== Injured / involved person =====
  section('Person involved');
  panel([
    ['Name', str('person')],
    ['Job / occupation', str('person_occupation')],
    ['Employer', str('employer')],
    ['Home address', str('person_address')],
  ], 2);

  // ===== What happened =====
  section('What happened');
  textBlock('Account of the incident', str('what'));
  textBlock('Injury / damage', str('injury'));
  textBlock('Part of body affected', str('body_part'));
  textBlock('Cause', [str('cause_imm'), str('cause_root')].filter(Boolean).join(' — '));

  // ===== Treatment & time off =====
  section('Treatment and time off');
  panel([
    ['First aid given by', str('fa')],
    ['Further treatment', str('treatment')],
    ['Hospital / clinic', str('hospital')],
    ['Time off work', str('time_off')],
    ['Back at work on', fmtD(str('return_date'))],
  ], 3);

  // ===== Witnesses =====
  const wit = data.witnesses;
  const witnesses: Array<{ name: string; statement: string }> = Array.isArray(wit)
    ? (wit as Array<Record<string, unknown>>).map((w) => ({ name: String(w.name ?? ''), statement: String(w.statement ?? '') }))
    : typeof wit === 'string' && wit ? [{ name: wit, statement: '' }] : [];
  if (witnesses.length) {
    section('Witnesses');
    for (const w of witnesses) textBlock(w.name || 'Witness', w.statement || '(no statement recorded)');
  }

  // ===== RIDDOR =====
  section('RIDDOR (reporting to HSE)', 20);
  const flags = Object.entries((opts.form.riddor_flags ?? {}) as Record<string, boolean>).filter(([, v]) => v);
  if (!opts.form.is_riddor || flags.length === 0) {
    callout('Not RIDDOR-reportable', 'The reporter confirmed none of the RIDDOR categories apply to this incident.', 'muted');
  } else {
    const cats = flags.map(([k]) => RIDDOR_LABELS[k] ?? k).join('; ');
    const ref = str('riddor_ref'), when = str('riddor_submitted_at');
    if (ref || when) {
      callout('Reported to HSE', `Category: ${cats}.  HSE reference: ${ref || '—'}.  Reported on: ${fmtD(when) || '—'}.`, 'success');
    } else {
      const days = flags.some(([k]) => k === 'over7day') ? 15 : 10;
      const base = new Date(str('date') || opts.form.performed_at);
      const deadline = new Date(base.getTime() + days * 86400000)
        .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      callout('RIDDOR-reportable — not yet reported',
        `Category: ${cats}.  Report to HSE at hse.gov.uk/riddor by ${deadline} (${days} days). Fatalities and specified injuries must be reported without delay.`, 'danger');
    }
  }

  // ===== Actions =====
  section('Action to prevent recurrence');
  textBlock('Action', str('action'));
  if (str('action_owner') || str('action_due')) {
    panel([['Who is doing it', str('action_owner')], ['To be done by', fmtD(str('action_due'))]], 2);
  }

  // ===== Investigation timeline =====
  const tl = (data.timeline as Array<{ at: string; by_name: string; note: string }> | undefined) ?? [];
  if (tl.length) {
    section('Investigation / follow-up log');
    for (const e of tl) textBlock(`${fmtD(e.at)} — ${e.by_name}`, e.note);
  }

  // ===== Photos =====
  if (photos.length > 0) {
    section('Photos', 60);
    const cols = 3, gap = 4;
    const w = (CONTENT_W - gap * (cols - 1)) / cols;
    let px = MARGIN, py = y, c = 0;
    for (const p of photos) {
      if (py + w > PAGE_H - 17) { doc.addPage(); py = cont(); px = MARGIN; c = 0; }
      try { doc.addImage(p, 'AUTO', px, py, w, w); } catch { /* noop */ }
      c++; px += w + gap;
      if (c === cols) { c = 0; px = MARGIN; py += w + gap; }
    }
    y = py + (c > 0 ? w + gap : 0) + 2;
  }

  // ===== Reported by =====
  section('Reported by', 30);
  y = drawSignatureBlock(doc, y, sigDataUrl,
    `${opts.form.performed_by_name ?? ''}${str('by_role') ? ` (${str('by_role')})` : ''}`,
    fmtDateLong(opts.form.performed_at));

  // ===== Manager review & close-out =====
  section('Manager review and close-out', 40);
  const closed = Boolean(data.closed_out);
  panel([
    ['Reviewed by', str('reviewed_by')],
    ['Review date', fmtD(str('reviewed_at'))],
    ['Status', closed ? `Closed ${fmtD(str('closed_at'))}` : 'Open — actions outstanding'],
  ], 3);
  textBlock('Findings / comments', str('review_notes'));
  if (!str('reviewed_by')) {
    // Blank box so a printed copy can be signed by hand.
    y = drawSignatureBlock(doc, y, null, 'Reviewer — name, signature & date', '');
  }


  return finishPdf(doc);
}

// ============================================================
// SUBBIE INDUCTION PDF
// ============================================================

export async function generateSubbieInductionPdf(opts: FormPdfOpts): Promise<Blob> {
  // Compliance pass Sep 2026: records the induction briefing itself
  // (CDM 2015 reg. 13(4)(c)), skills card, declaration, emergency
  // contact, first-aider status and inductor sign-off. Labels come from
  // lib/subbieInduction so ticks always match the form.
  const data = opts.form.data as Record<string, unknown>;
  const str = (k: string) => (data[k] == null ? '' : String(data[k]));
  const fmtD = (v: string) => {
    if (!v) return '';
    const d = new Date(v);
    return isNaN(d.getTime()) ? v : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const logoDataUrl = opts.orgLogoPath ? await fetchSignedDataUrl('org-logos', opts.orgLogoPath) : null;
  const sigDataUrl = opts.form.signature_url ? await fetchSignedDataUrl('signatures', opts.form.signature_url) : null;
  const photoPaths = (data.photo_paths as string[] | undefined) ?? [];
  const photos: string[] = [];
  for (const p of photoPaths) {
    const url = await fetchSignedDataUrl('subbie-induction-photos', p);
    if (url) photos.push(url);
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const subtitle = `${opts.site?.name ?? 'Site'} · ${fmtDateLong(opts.form.performed_at)}`;
  let y = drawHeader(doc, {
    orgName: opts.orgName, logoDataUrl,
    title: 'Sub-contractor induction',
    docType: 'Site induction record',
    subtitle,
  });
  const cont = () => drawHeader(doc, {
    orgName: opts.orgName, logoDataUrl, docType: 'Site induction record',
    title: 'Sub-contractor induction (cont.)', subtitle,
  });
  function ensure(n: number) { if (y + n > PAGE_H - 17) { doc.addPage(); y = cont(); } }
  function section(t: string, keep = 16) { ensure(14 + keep); y = drawSectionTitle(doc, t, y + 2); }
  function checks(labels: string[], values: unknown) {
    const vals = Array.isArray(values) ? values : [];
    for (let i = 0; i < labels.length; i++) {
      ensure(8);
      y = drawCheckRow(doc, MARGIN, y, !!vals[i], labels[i], CONTENT_W - 10);
    }
    y += 2;
  }

  // Data-protection line
  doc.setFontSize(7.5); doc.setFont('helvetica', 'italic'); doc.setTextColor(COLOUR_INK_SOFT);
  doc.text('Confidential — contains personal information. Keep secure; share only with people who need it (UK GDPR).', MARGIN, y - 3);
  y += 3;

  section('Person inducted');
  y = drawInfoPanel(doc, y, [
    ['Name', str('person_name')],
    ['Company', str('company_name')],
    ['Trade', str('trade')],
    ['Mobile', str('mobile')],
    ['Main contact', str('contact')],
    ['Address', str('address')],
    ['Emergency contact', [str('emergency_contact_name'), str('emergency_contact_phone')].filter(Boolean).join(' — ')],
  ], 3);

  section('Skills card & first aid');
  const cardType = str('card_type');
  y = drawInfoPanel(doc, y, [
    ['Card type', cardType],
    ['Card number', cardType === 'None held' ? '' : str('card_number')],
    ['Card expires', fmtD(str('card_expiry'))],
    ['First aider', data.first_aider ? 'Yes' : 'No'],
    ['First aid cert expires', data.first_aider ? fmtD(str('first_aid_expiry')) : ''],
  ], 3);

  section('Insurance', 30);
  checks(SUBBIE_INSURANCE, data.insurance);
  if (str('pl_expires') || str('el_expires')) {
    ensure(26);
    y = drawInfoPanel(doc, y, [
      ['Public Liability expires', fmtD(str('pl_expires'))],
      ["Employers' Liability expires", fmtD(str('el_expires'))],
    ], 2);
  }

  section('Competence checks', 30);
  checks(SUBBIE_COMPETENCE, data.competence);

  section('Induction topics covered', 40);
  if (Array.isArray(data.induction_topics)) {
    checks(INDUCTION_TOPICS, data.induction_topics);
  } else {
    doc.setFontSize(9.5); doc.setFont('helvetica', 'italic'); doc.setTextColor(COLOUR_INK_SOFT);
    doc.text('Not recorded (induction logged before topic checklist was introduced).', MARGIN, y);
    y += 8;
  }

  section('Declaration', 40);
  doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(COLOUR_INK);
  doc.text('By signing, the sub-contractor confirmed:', MARGIN, y);
  y += 5.5;
  for (const d of INDUCTION_DECLARATION) {
    const lines = wrapLines(doc, d, CONTENT_W - 6);
    ensure(lines.length * 4.4 + 2);
    doc.setFillColor(COLOUR_BRAND_DEEP);
    doc.circle(MARGIN + 1, y - 1.2, 0.7, 'F');
    doc.text(lines, MARGIN + 4, y);
    y += lines.length * 4.4 + 1;
  }
  y += 3;
  ensure(16);
  y = drawCheckRow(doc, MARGIN, y, !!data.understood, 'Sub-contractor has understood the induction', CONTENT_W - 10);
  y = drawCheckRow(doc, MARGIN, y, !!data.needs_support,
    `Interpreter / extra help needed${str('support_notes') ? ` — ${str('support_notes')}` : ''}`, CONTENT_W - 10);

  if (photos.length > 0) {
    section('Insurance / cards', 60);
    const cols = 3, gap = 4;
    const w = (CONTENT_W - gap * (cols - 1)) / cols;
    let px = MARGIN, py = y, c = 0;
    for (const p of photos) {
      if (py + w > PAGE_H - 17) { doc.addPage(); py = cont(); px = MARGIN; c = 0; }
      try { doc.addImage(p, 'AUTO', px, py, w, w); } catch { /* noop */ }
      c++; px += w + gap;
      if (c === cols) { c = 0; px = MARGIN; py += w + gap; }
    }
    y = py + (c > 0 ? w + gap : 0) + 2;
  }

  section('Signatures', 34);
  const half = (CONTENT_W - 6) / 2;
  doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(COLOUR_INK_SOFT);
  doc.text('SUB-CONTRACTOR', MARGIN, y);
  doc.text('INDUCTION GIVEN BY', MARGIN + half + 6, y);
  y += 2;
  drawSignatureBlock(doc, y, sigDataUrl, str('person_name'), fmtDateLong(opts.form.performed_at), MARGIN, half);
  y = drawSignatureBlock(doc, y, (data.inductor_signature_data_url as string | null) ?? null,
    str('inductor_name') || opts.form.performed_by_name || '', fmtDateLong(opts.form.performed_at), MARGIN + half + 6, half);

  return finishPdf(doc);
}

// ============================================================
// RAMS TEMPLATE PDF (preset → site-specific RAMS document)
// ============================================================

interface RamsPdfOpts {
  orgName: string;
  orgLogoPath: string | null;
  title: string;
  trade: string;
  reviewChecks: boolean[];                 // 5 booleans
  pcSignatureDataUrl: string | null;       // signature of person preparing the RAMS
  pcName: string;
  fields: {
    site_name: string;
    plot_or_area: string;
    client: string;
    company: string;
    company_contact: string;
    company_mobile: string;
    prepared_by: string;
    date: string;
    scope: string;
    hazards: string;     // freeform multi-line text — preserved as-is
    sequence: string;    // newline-separated steps
    equipment: string;   // one per line
    ppe: string;         // one per line
    emergency: string;
    notes: string;
  };
  // When false, suppresses the yellow "DRAFT — REVIEW REQUIRED BEFORE USE"
  // banner. Passed as false from the "Generate & save" path because by
  // that point the preparer has signed and completed every review tick,
  // so the doc is issued — calling it a draft would be misleading.
  // Defaults to true so any callers that don't set it keep the previous
  // behaviour (safer fallback for guidance-document use cases).
  isDraft?: boolean;
}

export async function generateRamsTemplatePdf(opts: RamsPdfOpts): Promise<Blob> {
  // Layout refresh 28 Sep 2026 (Will's feedback: logo cramped against the
  // title, "Page 1 of 1" on every page, hazards hard to scan). Changes:
  //  - logo gets its own band with clear space before the title
  //  - page numbers stamped at the end so they read "Page 2 of 3"
  //  - project + contractor details in one tidy 3-column panel
  //  - hazard cards: coloured severity stripe, bold hazard title,
  //    controls as a bullet list (was one run-on sentence)
  //  - equipment + PPE side by side; emergency in a red-tinted panel
  //  - section headings with an accent bar and a hairline rule
  const logoDataUrl = opts.orgLogoPath
    ? await fetchSignedDataUrl('org-logos', opts.orgLogoPath)
    : null;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const BOTTOM = PAGE_H - 17;
  const subtitle = `${opts.fields.site_name || 'Site'}  ·  ${opts.fields.date}`;

  function drawRamsHeader(cont: boolean): number {
    let top = 12;
    let logoBottom = top;
    if (logoDataUrl) {
      try {
        const props = doc.getImageProperties(logoDataUrl);
        const aspect = props.width / props.height;
        const maxW = cont ? 26 : 34, maxH = cont ? 12 : 16;
        let w = maxW, h = maxW / aspect;
        if (h > maxH) { h = maxH; w = maxH * aspect; }
        doc.addImage(logoDataUrl, 'AUTO', MARGIN, top, w, h);
        logoBottom = top + h;
      } catch { logoBottom = top; }
    }
    // Org name + doc type, top right, quiet.
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(COLOUR_INK);
    doc.text(opts.orgName || '', PAGE_W - MARGIN, top + 4, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(COLOUR_INK_SOFT);
    doc.text('Risk Assessment & Method Statement', PAGE_W - MARGIN, top + 8.5, { align: 'right' });

    let y = Math.max(logoBottom, top + 10) + (cont ? 8 : 11);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(cont ? 14 : 20);
    doc.setTextColor(COLOUR_INK);
    const titleLines = wrapLines(doc, cont ? `${opts.title} (continued)` : opts.title, CONTENT_W);
    doc.text(titleLines, MARGIN, y);
    y += (titleLines.length - 1) * (cont ? 6 : 8);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(cont ? 9 : 11);
    doc.setTextColor(COLOUR_INK_SOFT);
    y += cont ? 5 : 6.5;
    doc.text(subtitle, MARGIN, y);
    y += 4;
    doc.setDrawColor(COLOUR_BRAND_DEEP);
    doc.setLineWidth(cont ? 0.3 : 0.8);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    return y + (cont ? 7 : 9);
  }

  let y = drawRamsHeader(false);

  function newPage() {
    doc.addPage();
    y = drawRamsHeader(true);
  }
  function ensureSpace(needed: number) {
    if (y + needed > BOTTOM) newPage();
  }

  function section(title: string, keepWith = 0) {
    ensureSpace(14 + keepWith);
    doc.setFillColor(COLOUR_BRAND_DEEP);
    doc.rect(MARGIN, y - 3.6, 1.2, 4.6, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLOUR_BRAND_DEEP);
    doc.text(title.toUpperCase(), MARGIN + 3.5, y);
    doc.setDrawColor(COLOUR_BORDER);
    doc.setLineWidth(0.25);
    doc.line(MARGIN, y + 2.5, PAGE_W - MARGIN, y + 2.5);
    y += 8;
  }

  function paragraph(text: string) {
    if (!text || !text.trim()) return;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLOUR_INK);
    const lines = wrapLines(doc, text, CONTENT_W);
    for (const line of lines) {
      ensureSpace(5);
      doc.text(line, MARGIN, y);
      y += 4.6;
    }
    y += 5;
  }

  // ===== Details panel (project + contractor) =====
  {
    const cells: Array<[string, string]> = [
      ['Site / project', opts.fields.site_name],
      ['Plot / area', opts.fields.plot_or_area],
      ['Client', opts.fields.client],
      ['Trade', opts.trade],
      ['Prepared by', opts.fields.prepared_by],
      ['Date', opts.fields.date],
      ['Company', opts.fields.company],
      ['Main contact', opts.fields.company_contact],
      ['Mobile', opts.fields.company_mobile],
    ];
    const cols = 3, pad = 5, gap = 5;
    const colW = (CONTENT_W - pad * 2 - gap * (cols - 1)) / cols;
    doc.setFontSize(10);
    const rowHeights: number[] = [];
    for (let r = 0; r < cells.length / cols; r++) {
      let h = 0;
      for (let c = 0; c < cols; c++) {
        const [, v] = cells[r * cols + c];
        h = Math.max(h, wrapLines(doc, v || '—', colW).length);
      }
      rowHeights.push(4 + h * 4.3 + 4);
    }
    const panelH = pad + rowHeights.reduce((a, b) => a + b, 0) + pad - 4;
    ensureSpace(panelH + 6);
    doc.setFillColor('#F8FAFC');
    doc.setDrawColor(COLOUR_BORDER);
    doc.setLineWidth(0.25);
    doc.roundedRect(MARGIN, y, CONTENT_W, panelH, 2, 2, 'FD');
    let ry = y + pad + 3;
    rowHeights.forEach((rh, r) => {
      for (let c = 0; c < cols; c++) {
        const [label, value] = cells[r * cols + c];
        const x = MARGIN + pad + c * (colW + gap);
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(COLOUR_INK_SOFT);
        doc.text(label.toUpperCase(), x, ry);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(value ? COLOUR_INK : COLOUR_INK_SOFT);
        doc.text(wrapLines(doc, value || '—', colW), x, ry + 4.5);
      }
      ry += rh;
    });
    y += panelH + 9;
  }

  // ===== DRAFT banner — only on un-issued docs =====
  if (opts.isDraft !== false) {
    ensureSpace(16);
    doc.setFillColor('#FFF8E5');
    doc.setDrawColor(COLOUR_WARN);
    doc.setLineWidth(0.4);
    doc.roundedRect(MARGIN, y, CONTENT_W, 11, 1.5, 1.5, 'FD');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLOUR_WARN);
    doc.text('DRAFT — REVIEW REQUIRED BEFORE USE', MARGIN + 4, y + 4.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLOUR_INK);
    doc.setFontSize(8);
    doc.text('Every section below is a starting point. Make site-specific. Verify with the team. Sign off.', MARGIN + 4, y + 8.5);
    y += 17;
  }

  // ===== 1. Scope =====
  section('1. Scope of work');
  paragraph(opts.fields.scope);

  // ===== 2. Hazards & controls =====
  type Hz = { risk: 'High' | 'Med' | 'Low' | null; title: string; controls: string[] };
  const hazards: Hz[] = opts.fields.hazards
    .split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean)
    .map((block) => {
      let risk: Hz['risk'] = null;
      let body = block;
      const m = block.match(/^\[(High|Med|Low)\]\s*/i);
      if (m) {
        const r = m[1].toLowerCase();
        risk = r.startsWith('h') ? 'High' : r.startsWith('m') ? 'Med' : 'Low';
        body = block.slice(m[0].length);
      }
      const ci = body.search(/controls?\s*:/i);
      const title = (ci >= 0 ? body.slice(0, ci) : body).replace(/\s+/g, ' ').trim();
      const ctrlText = ci >= 0 ? body.slice(ci).replace(/^controls?\s*:/i, '') : '';
      const controls = ctrlText.split(/;\s*|\n+/).map((c) => c.trim().replace(/\.$/, '')).filter(Boolean)
        .map((c) => c.charAt(0).toUpperCase() + c.slice(1));
      return { risk, title, controls };
    });

  const cardPadX = 5, stripeW = 2.2, badgeW = 16;
  const titleW = CONTENT_W - stripeW - cardPadX * 2 - badgeW - 4;
  const ctrlW = CONTENT_W - stripeW - cardPadX * 2 - 4;
  function measureHazard(h: Hz) {
    doc.setFontSize(10.5); doc.setFont('helvetica', 'bold');
    const tl = wrapLines(doc, h.title || 'Hazard', titleW);
    doc.setFontSize(9.5); doc.setFont('helvetica', 'normal');
    const cl = h.controls.map((c) => wrapLines(doc, c, ctrlW));
    const ctrlLines = cl.reduce((a, l) => a + l.length, 0);
    const height = 4 + tl.length * 4.6 + (h.controls.length ? 5.5 + ctrlLines * 4.1 + (h.controls.length - 1) * 0.8 : 0) + 1.5;
    return { tl, cl, height };
  }
  const firstH = hazards.length ? measureHazard(hazards[0]).height : 0;
  section('2. Hazards and controls', firstH);
  for (const h of hazards) {
    const { tl, cl, height } = measureHazard(h);
    ensureSpace(height + 4);
    const colour = h.risk === 'High' ? COLOUR_DANGER : h.risk === 'Med' ? COLOUR_WARN : h.risk === 'Low' ? COLOUR_SUCCESS : COLOUR_BORDER;
    doc.setFillColor('#FFFFFF');
    doc.setDrawColor(COLOUR_BORDER);
    doc.setLineWidth(0.25);
    doc.roundedRect(MARGIN, y, CONTENT_W, height, 1.8, 1.8, 'FD');
    doc.setFillColor(colour);
    doc.roundedRect(MARGIN, y, stripeW + 1.8, height, 1.8, 1.8, 'F');
    doc.setFillColor('#FFFFFF');
    doc.rect(MARGIN + stripeW, y + 0.15, 1.9, height - 0.3, 'F');
    const x = MARGIN + stripeW + cardPadX;
    let iy = y + 6.5;
    if (h.risk) drawSeverityBadge(doc, PAGE_W - MARGIN - cardPadX - badgeW, y + 3.2, h.risk);
    doc.setFontSize(10.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(COLOUR_INK);
    doc.text(tl, x, iy);
    iy += (tl.length - 1) * 4.6;
    if (h.controls.length) {
      iy += 5.5;
      doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(COLOUR_INK_SOFT);
      doc.text('CONTROL MEASURES', x, iy);
      iy += 4.3;
      doc.setFontSize(9.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(COLOUR_INK);
      for (const lines of cl) {
        doc.setFillColor(colour);
        doc.circle(x + 1, iy - 1.1, 0.6, 'F');
        doc.text(lines, x + 4, iy);
        iy += lines.length * 4.1 + 0.8;
      }
    }
    y += height + 3.5;
  }
  y += 4;

  // ===== 3. Method / sequence =====
  const steps = opts.fields.sequence.split(/\n/).map((s) => s.trim()).filter(Boolean)
    .map((s) => s.replace(/^\s*\d+[.)]\s*/, ''));
  section('3. Method / sequence of work', 10);
  const stepIndent = 9;
  steps.forEach((step, idx) => {
    doc.setFontSize(10);
    const lines = wrapLines(doc, step, CONTENT_W - stepIndent);
    const blockH = lines.length * 4.4 + 3.2;
    ensureSpace(blockH + 1);
    drawNumberCircle(doc, MARGIN, y + 1.6, idx + 1);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(COLOUR_INK);
    doc.text(lines, MARGIN + stepIndent, y + 1.6);
    y += blockH;
  });
  y += 6;

  // ===== 4 + 5. Equipment and PPE — side by side =====
  const eqs = opts.fields.equipment.split(/\n/).map((s) => s.trim()).filter(Boolean);
  const ppes = opts.fields.ppe.split(/\n/).map((s) => s.trim()).filter(Boolean);
  {
    const half = (CONTENT_W - 8) / 2;
    doc.setFontSize(10);
    const eqL = eqs.map((e) => wrapLines(doc, e, half - 5));
    const ppL = ppes.map((e) => wrapLines(doc, e, half - 5));
    const listH = (ls: string[][]) => ls.reduce((a, l) => a + l.length * 4.3 + 1, 0);
    const blockH = 10 + Math.max(listH(eqL), listH(ppL));
    ensureSpace(Math.min(blockH, BOTTOM - 60) + 4);
    const heading = (t: string, x: number) => {
      doc.setFillColor(COLOUR_BRAND_DEEP);
      doc.rect(x, y - 3.6, 1.2, 4.6, 'F');
      doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(COLOUR_BRAND_DEEP);
      doc.text(t.toUpperCase(), x + 3.5, y);
      doc.setDrawColor(COLOUR_BORDER); doc.setLineWidth(0.25);
      doc.line(x, y + 2.5, x + half, y + 2.5);
    };
    heading('4. Equipment required', MARGIN);
    heading('5. PPE required', MARGIN + half + 8);
    y += 8;
    const drawList = (ls: string[][], x: number, startY: number) => {
      let ly = startY;
      doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(COLOUR_INK);
      for (const lines of ls) {
        doc.setFillColor(COLOUR_BRAND_DEEP);
        doc.circle(x + 1, ly - 1.2, 0.7, 'F');
        doc.text(lines, x + 4, ly);
        ly += lines.length * 4.3 + 1;
      }
      return ly;
    };
    const endL = drawList(eqL, MARGIN, y);
    const endR = drawList(ppL, MARGIN + half + 8, y);
    y = Math.max(endL, endR) + 6;
  }

  // ===== 6. Emergency — red-tinted panel so it stands out =====
  if (opts.fields.emergency.trim()) {
    doc.setFontSize(10);
    const lines = wrapLines(doc, opts.fields.emergency.trim(), CONTENT_W - 12);
    const panelH = lines.length * 4.6 + 8;
    section('6. Emergency arrangements', panelH);
    doc.setFillColor('#FDF2F2');
    doc.setDrawColor('#F3D4D3');
    doc.setLineWidth(0.25);
    doc.roundedRect(MARGIN, y - 1, CONTENT_W, panelH, 1.8, 1.8, 'FD');
    doc.setFillColor(COLOUR_DANGER);
    doc.rect(MARGIN, y - 1, 1.6, panelH, 'F');
    doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(COLOUR_INK);
    doc.text(lines, MARGIN + 6, y + 4.5);
    y += panelH + 8;
  }

  // ===== 7. Notes =====
  if (opts.fields.notes.trim()) {
    section('7. Site-specific notes');
    paragraph(opts.fields.notes.trim());
  }

  // ===== Pre-use review checklist =====
  const checklistItems = [
    'Site condition checked — physical conditions match the assumed scope',
    'Utilities checked — gas / electric / water / drainage drawings reviewed and CAT-scanned where relevant',
    'Competence verified — every person on this work has the cards / training to do it',
    'Control measures reviewed — every hazard above has a workable control on this site',
    'I am competent to prepare and sign off this RAMS — I have the relevant training, experience and authority',
  ];
  section('Pre-use review checklist', 40);
  doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(COLOUR_INK_SOFT);
  doc.text('Tick each box once you have personally verified:', MARGIN, y);
  y += 5;
  for (let ci = 0; ci < checklistItems.length; ci++) {
    const innerPadX = 5;
    doc.setFontSize(9.5);
    const lines = wrapLines(doc, checklistItems[ci], CONTENT_W - innerPadX * 2 - 9);
    const blockH = Math.max(9, lines.length * 4 + 5);
    ensureSpace(blockH + 2);
    doc.setFillColor('#FFFFFF'); doc.setDrawColor(COLOUR_BORDER); doc.setLineWidth(0.25);
    doc.roundedRect(MARGIN, y, CONTENT_W, blockH, 1.5, 1.5, 'FD');
    drawCheckbox(doc, MARGIN + innerPadX, y + blockH / 2 + 2, !!opts.reviewChecks[ci], 4.5);
    doc.setFontSize(9.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(COLOUR_INK);
    doc.text(lines, MARGIN + innerPadX + 9, y + blockH / 2 - (lines.length - 1) * 2 + 1.4);
    y += blockH + 2.5;
  }
  y += 6;

  // ===== Sign-off =====
  section('Sign-off', 30);
  const boxW = CONTENT_W / 2 + 10;
  doc.setDrawColor(COLOUR_BORDER); doc.setLineWidth(0.3);
  doc.roundedRect(MARGIN, y, boxW, 24, 1.5, 1.5, 'S');
  if (opts.pcSignatureDataUrl) {
    try { doc.addImage(opts.pcSignatureDataUrl, 'AUTO', MARGIN + 2, y + 1.5, boxW - 4, 15); } catch { /* noop */ }
  }
  doc.setDrawColor(COLOUR_BORDER);
  doc.line(MARGIN + 3, y + 18, MARGIN + boxW - 3, y + 18);
  doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(COLOUR_INK);
  doc.text(opts.pcName || opts.fields.prepared_by || '', MARGIN + 3, y + 22);
  doc.setFont('helvetica', 'normal'); doc.setTextColor(COLOUR_INK_SOFT);
  doc.text(opts.fields.date, MARGIN + boxW - 3, y + 22, { align: 'right' });
  doc.setFontSize(8);
  doc.text(
    opts.pcSignatureDataUrl ? 'Signed digitally by the preparer' : 'Preparer — name, signature & date',
    MARGIN + boxW + 5, y + 22,
  );
  y += 32;


  return finishPdf(doc);
}

// ============================================================
// DOCUMENT PDF (generic fill-in template renderer)
// ============================================================

interface DocumentPdfOpts {
  orgName: string;
  orgLogoPath: string | null;
  title: string;
  category: string;
  scope: 'org' | 'site';
  siteName: string | null;
  fields: Array<{ key: string; label: string; value: string }>;
  sections: Array<{ heading: string; body: string }>;
  signatureDataUrl: string | null;
  signatoryName: string;
  date: string;
  expiresAt?: string | null;
  notes?: string | null;
  // Optional — lets the house-style renderer pull the correct
  // purpose paragraph and CDM-style subtitle from HOUSE_STYLE_
  // PURPOSES / HOUSE_STYLE_SUBTITLES. Falls back to generic labels
  // if not supplied so callers that don't have a template ID still
  // get a valid PDF.
  templateId?: string;
}

export async function generateDocumentPdf(opts: DocumentPdfOpts): Promise<Blob> {
  const logoDataUrl = opts.orgLogoPath
    ? await fetchSignedDataUrl('org-logos', opts.orgLogoPath)
    : null;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  const projectName = opts.siteName ?? opts.orgName;
  const templateId = opts.templateId ?? '';
  const spec = makeHouseSpec({
    orgName: opts.orgName,
    logoDataUrl,
    templateId,
    docTitle: opts.title,
    docTypeUpper: opts.title.toUpperCase(),
    projectName,
    version: 'VERSION 1.0',
    issueDate: opts.date,
    docRef: `${opts.category.toUpperCase()}  ·  ${projectName.toUpperCase()}  ·  V1.0`,
    // Short registers / policies are usually printed and passed
    // around freely — don't stamp them CONFIDENTIAL by default.
    // Site-scope CDM docs (Welfare, Fire, Emergency, Traffic, PCI)
    // do get the badge.
    confidential: opts.scope === 'site' && opts.category === 'cdm',
  });

  // ===== Cover =====
  drawHouseStyleCover(doc, spec);

  // ===== Page 2 — Document Control and Revisions =====
  doc.addPage();
  let y = drawHouseRunningHeader(doc, spec);

  // TODO(pdf-house-style): populate this from real
  // doc-supersede history once the DB carries it. Until then, seed
  // a single "First issue" row so the table renders on every fresh
  // doc — matches the reference visually and gives inspectors
  // something to read.
  const revisions: Revision[] = [
    {
      rev: '1.0',
      date: opts.date,
      preparedBy: opts.signatoryName || '—',
      approvedBy: opts.signatoryName || '—',
      summary: 'First issue.',
    },
  ];
  y = drawHouseRevisionSection(
    doc,
    y,
    `${opts.title} — issued by ${opts.orgName} for use on the project referenced above. This section records the revision history for the document; every subsequent update supersedes the previous version and is captured here.`,
    revisions,
  );

  function ensureSpace(needed: number): void {
    if (y + needed > PAGE_H - 20) {
      doc.addPage();
      y = drawHouseRunningHeader(doc, spec);
    }
  }

  function paragraph(text: string): void {
    if (!text || !text.trim()) return;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(COLOUR_INK);
    doc.setCharSpace(0);
    const lines = wrapLines(doc, text, CONTENT_W);
    ensureSpace(lines.length * 4.5 + 2);
    doc.text(lines, MARGIN, y);
    y += lines.length * 4.5 + 3;
  }

  // ===== Filled fields block — rendered as a sub-section table
  // rather than the old two-column form. Reads as a clean document
  // header row-by-row. =====
  if (opts.fields.length > 0) {
    ensureSpace(20);
    y += 3;
    y = drawHouseSubSection(doc, y, '0.1', 'Document details');
    doc.setCharSpace(0);
    for (const f of opts.fields) {
      const value = f.value?.trim() || '—';
      const valueLines = wrapLines(doc, value, CONTENT_W - 62);
      const rowH = Math.max(7.5, valueLines.length * 4.5 + 3);
      ensureSpace(rowH);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(COLOUR_INK_SOFT);
      doc.text(f.label, MARGIN, y + 4);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(COLOUR_INK);
      doc.text(valueLines, MARGIN + 60, y + 4);
      doc.setDrawColor(COLOUR_BORDER);
      doc.setLineWidth(0.15);
      doc.line(MARGIN, y + rowH, PAGE_W - MARGIN, y + rowH);
      y += rowH;
    }
    y += 4;
  }

  // ===== Expiry badge — small, subtle, above the sections =====
  if (opts.expiresAt) {
    ensureSpace(14);
    doc.setFillColor('#FFF8E5');
    doc.setDrawColor(COLOUR_WARN);
    doc.setLineWidth(0.3);
    doc.roundedRect(MARGIN, y, CONTENT_W, 9, 1.5, 1.5, 'FD');
    drawTracked(doc, 'EXPIRES', MARGIN + 4, y + 6, {
      size: 8, tracking: 0.8, bold: true, colour: COLOUR_WARN,
    });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(COLOUR_INK);
    doc.setCharSpace(0);
    doc.text(opts.expiresAt, MARGIN + 30, y + 6);
    y += 14;
  }

  // ===== Numbered sections (page 3+) =====
  for (let i = 0; i < opts.sections.length; i++) {
    const sec = opts.sections[i];
    const cleanHeading = sec.heading.replace(/^\d+\.\s*/, '');
    const num = i + 1;

    ensureSpace(24);
    // Short docs don't need an intro under every heading — the
    // heading itself carries the meaning. Pass undefined so the
    // section header renders without a stub paragraph.
    y = drawHouseSection(doc, y, num, cleanHeading, undefined);

    const body = (sec.body ?? '').trim();
    if (!body) {
      y = drawHouseEmptyPlaceholder(doc, y);
    } else {
      const bulletLines = body.split(/\n/).map((l) => l.trim()).filter(Boolean);
      const looksLikeBullets = bulletLines.length > 1
        && bulletLines.every((l) => /^([•·\-*]\s+|\d+\.\s+)/.test(l));
      if (looksLikeBullets) {
        for (const l of bulletLines) {
          const text = l.replace(/^([•·\-*]\s+|\d+\.\s+)/, '');
          ensureSpace(6);
          y = drawHouseBullet(doc, y, text);
        }
        y += 1;
      } else {
        const paragraphs = body.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
        if (paragraphs.length > 1) {
          for (const p of paragraphs) paragraph(p);
        } else {
          paragraph(body);
        }
      }
    }
    y += 3;
  }

  if (opts.notes && opts.notes.trim()) {
    ensureSpace(18);
    y = drawHouseSection(doc, y, opts.sections.length + 1, 'Notes', undefined);
    paragraph(opts.notes.trim());
  }

  // ===== Sign-off block =====
  if (opts.signatureDataUrl || opts.signatoryName) {
    ensureSpace(50);
    y = drawHouseSection(doc, y, opts.sections.length + (opts.notes && opts.notes.trim() ? 2 : 1),
      'Issued by', undefined);
    doc.setDrawColor(COLOUR_BORDER);
    doc.setLineWidth(0.3);
    const sigBoxX = MARGIN;
    const sigBoxW = (CONTENT_W / 2) + 10;
    doc.roundedRect(sigBoxX, y, sigBoxW, 24, 1.2, 1.2, 'S');
    if (opts.signatureDataUrl) {
      try {
        doc.addImage(opts.signatureDataUrl, 'AUTO', sigBoxX + 2, y + 1, sigBoxW - 4, 15);
      } catch { /* noop */ }
    }
    if (opts.signatoryName) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(COLOUR_INK);
      doc.setCharSpace(0);
      doc.text(opts.signatoryName, sigBoxX + 2, y + 20);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(COLOUR_INK_SOFT);
      doc.text(opts.date, sigBoxX + sigBoxW - 2, y + 20, { align: 'right' });
    }
    y += 28;
    drawTracked(doc, 'SIGNATURE  ·  NAME  ·  DATE', sigBoxX, y, {
      size: 7, tracking: 0.6, colour: COLOUR_INK_SOFT,
    });
    y += 8;
  }


  // Two-pass page numbers + brand attribution on last page only.
  stampHousePageNumbers(doc, spec, { skipCover: true, brandAttribution: true });
  return doc.output('blob');
}

// ============================================================
// PERMIT PDF (issued + completion checks)
// ============================================================

interface PermitPdfOpts {
  orgName: string;
  orgLogoPath: string | null;
  title: string;
  permitType: string;
  siteName: string;
  issuedToName: string;
  issuedToCompany: string | null;
  issuedByName: string;
  issuedAt: string;
  expiresAt: string;
  description: string;
  prerequisites: string[];
  controls: string[];
  signOffChecks: Array<{ label: string; checked: boolean }>;
  fireWatchMinutes?: number | null;
  signatureDataUrl: string | null;
  notes?: string | null;
  closing?: {
    completionChecks: Array<{ label: string; checked: boolean }>;
    closedByName: string;
    closedAt: string;
    closingSignatureDataUrl: string | null;
    closingNotes?: string | null;
  };
}

export async function generatePermitPdf(opts: PermitPdfOpts): Promise<Blob> {
  const logoDataUrl = opts.orgLogoPath
    ? await fetchSignedDataUrl('org-logos', opts.orgLogoPath)
    : null;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  const status = opts.closing ? 'CLOSED' : 'ISSUED';
  const subtitle = `${opts.siteName} · ${opts.issuedAt} · ${status}`;

  function drawDocHeader(title: string) {
    return drawHeader(doc, {
      orgName: opts.orgName,
      logoDataUrl,
      title,
      subtitle,
      docType: 'Permit to work',
    });
  }

  let y = drawDocHeader(`${opts.title}`);

  function ensureSpace(needed: number) {
    if (y + needed > PAGE_H - 20) {
      doc.addPage();
      y = drawDocHeader(`${opts.title} (cont.)`);
    }
  }

  function section(heading: string, keepWith = 10) {
    ensureSpace(14 + keepWith);
    y = drawSectionTitle(doc, heading, y + 5);
  }

  function paragraph(text: string) {
    if (!text || !text.trim()) return;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLOUR_INK);
    const lines = wrapLines(doc, text, CONTENT_W);
    ensureSpace(lines.length * 4.4 + 2);
    doc.text(lines, MARGIN, y);
    y += lines.length * 4.4 + 2.5;
  }

  function checklist(items: Array<{ label: string; checked: boolean }>) {
    for (const item of items) {
      ensureSpace(7);
      drawCheckbox(doc, MARGIN, y + 1, item.checked, 4.5);
      const lines = wrapLines(doc, item.label, CONTENT_W - 10);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(COLOUR_INK);
      doc.text(lines, MARGIN + 8, y);
      y += lines.length * 4 + 2;
    }
    y += 4;
  }

  // ===== Cover info =====
  y = drawInfoPanel(doc, y, [
    ['Permit type', opts.permitType.replace(/_/g, ' ')],
    ['Site', opts.siteName],
    ['Issued to', opts.issuedToName + (opts.issuedToCompany ? ` (${opts.issuedToCompany})` : '')],
    ['Issued by', opts.issuedByName],
    ['Issued at', opts.issuedAt],
    ['Expires', opts.expiresAt],
  ], 3);

  // ===== Status banner =====
  ensureSpace(14);
  const statusFill = opts.closing ? '#EAF3EE' : '#FFF8E5';
  const statusStroke = opts.closing ? COLOUR_SUCCESS : COLOUR_WARN;
  doc.setFillColor(statusFill);
  doc.setDrawColor(statusStroke);
  doc.setLineWidth(0.4);
  doc.roundedRect(MARGIN, y, CONTENT_W, 11, 1.5, 1.5, 'FD');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(statusStroke);
  doc.text(opts.closing ? 'PERMIT CLOSED' : 'PERMIT ISSUED', MARGIN + 4, y + 4.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(COLOUR_INK);
  doc.setFontSize(8);
  doc.text(
    opts.closing
      ? `Closed ${opts.closing.closedAt} by ${opts.closing.closedByName}.`
      : 'This permit authorises only the work and operative named above.',
    MARGIN + 4, y + 8.5
  );
  y += 15;

  section('Description');
  paragraph(opts.description);

  section('Prerequisites verified');
  for (const p of opts.prerequisites) paragraph(`• ${p}`);

  section('Controls during work');
  for (const c of opts.controls) paragraph(`• ${c}`);

  if (opts.fireWatchMinutes && opts.fireWatchMinutes > 0) {
    ensureSpace(12);
    doc.setFillColor('#FFF8E5');
    doc.setDrawColor(COLOUR_WARN);
    doc.setLineWidth(0.4);
    doc.roundedRect(MARGIN, y, CONTENT_W, 9, 1.5, 1.5, 'FD');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLOUR_WARN);
    doc.text('FIRE WATCH', MARGIN + 4, y + 5.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLOUR_INK);
    doc.text(`${opts.fireWatchMinutes} minutes minimum after last spark / flame.`, MARGIN + 35, y + 5.5);
    y += 13;
  }

  section('Issuer sign-off');
  checklist(opts.signOffChecks);

  if (opts.notes && opts.notes.trim()) {
    section('Notes');
    paragraph(opts.notes);
  }

  // ===== Signature on issue =====
  section('Issued by', 30);
  y = drawSignatureBlock(doc, y, opts.signatureDataUrl, opts.issuedByName, opts.issuedAt);

  // ===== Closure section =====
  if (opts.closing) {
    section('Completion checks');
    checklist(opts.closing.completionChecks);

    if (opts.closing.closingNotes) {
      section('Closing notes');
      paragraph(opts.closing.closingNotes);
    }

    section('Closed by', 30);
    y = drawSignatureBlock(doc, y, opts.closing.closingSignatureDataUrl, opts.closing.closedByName, opts.closing.closedAt);
  }

  return finishPdf(doc);
}

// ============================================================
// HOUSE-STYLE HELPERS (Phase 2 — long-form doc PDFs)
// ============================================================
// Extracted from the Quarters CPP reference the founder uploaded.
// Every long-form doc — CPP, PCI, PQQ, Site Welfare / Fire /
// Emergency / Traffic Management — uses these so the output reads
// as a single visual family instead of the mismatched Phase-0 style
// (chunky coloured section bars, "Generated by App" footers,
// broken pagination).
//
// Design language:
//   - Tracked (letter-spaced) small-caps for masthead + running
//     header + sub-section headings. jsPDF only bundles Helvetica /
//     Times / Courier so we can't ship a display serif; we get the
//     magazine feel via size + weight + tracking + caps.
//   - Big bold Helvetica for doc title + numbered section headings.
//   - Hairline rules for structure, not coloured bars.
//   - Generous whitespace; single-column body.
//   - "Prepared with Safe and Sorted" attribution on the last page
//     only — quiet, no logo, replaces the old "Generated by App"
//     footer that appeared on every page.
//   - Two-pass page numbering (stampHousePageNumbers) — the
//     renderer builds the whole doc, then walks pages and paints
//     "Page X of Y" once the total is known. Fixes task #152
//     (the "Page 1 of 1 on every page" bug).
// ============================================================

interface HouseStyleSpec {
  orgName: string;            // 'Safe and Sorted' — customer's brand (mixed case)
  orgNameUpper: string;       // 'SAFE AND SORTED' — masthead + running header
  logoDataUrl: string | null;
  docTitle: string;           // 'Construction Phase Plan'  — cover title (mixed case)
  docTypeUpper: string;       // 'CONSTRUCTION PHASE PLAN'  — running header
  subtitle: string;           // 'CDM 2015 · REGULATION 12 · PRINCIPAL CONTRACTOR\'S PLAN'
  purpose: string;            // One paragraph on the cover — what this doc is / how it's used
  projectName: string;        // 'Follifoot — 3 Houses'  (mixed case, cover project block)
  projectNameUpper: string;   // 'FOLLIFOOT — 3 HOUSES'  (tracked caps, running header)
  version: string;            // 'VERSION 1.0'
  issueMonthUpper: string;    // 'JULY 2026'
  issueDate: string;          // '10 July 2026' — footer left
  docRef: string;             // 'CPP · FOLLIFOOT · 1.0' — footer left
  confidential?: boolean;     // running header shows 'CONFIDENTIAL' when true (default)
}

/**
 * Draw a tracked (letter-spaced) line of text. jsPDF exposes
 * `setCharSpace` — we bump it up to 0.5–1.5mm and reset to 0 after
 * every call so nothing else in the doc inherits the tracking.
 */
function drawTracked(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  opts: {
    size: number;
    align?: 'left' | 'center' | 'right';
    tracking?: number;
    colour?: string;
    bold?: boolean;
  },
): void {
  doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
  doc.setFontSize(opts.size);
  doc.setTextColor(opts.colour ?? COLOUR_INK);
  const cs = opts.tracking ?? 0.5;
  // jsPDF's align ignores character spacing, so right/centre-aligned
  // tracked text ran off the page. Measure the true width ourselves.
  const w = doc.getTextWidth(text) + cs * Math.max(0, text.length - 1);
  const x0 = opts.align === 'right' ? x - w : opts.align === 'center' ? x - w / 2 : x;
  doc.setCharSpace(cs);
  doc.text(text, x0, y);
  doc.setCharSpace(0);
}

/**
 * Cover page — small-caps org masthead at the top, big bold doc
 * title in the upper-middle, tracked CDM-style subtitle underneath,
 * purpose paragraph centred, project title block at the bottom
 * third with a hairline rule above it (project name · version ·
 * month year).
 */
function drawHouseStyleCover(doc: jsPDF, spec: HouseStyleSpec): void {
  // Masthead — logo (small, centred) or org name in tracked caps
  const topY = 34;
  if (spec.logoDataUrl) {
    try {
      const props = doc.getImageProperties(spec.logoDataUrl);
      const aspect = props.width / props.height;
      const maxW = 42;
      const maxH = 16;
      let w = maxW;
      let h = maxW / aspect;
      if (h > maxH) { h = maxH; w = maxH * aspect; }
      doc.addImage(spec.logoDataUrl, 'AUTO', (PAGE_W - w) / 2, topY - 10, w, h);
    } catch {
      drawTracked(doc, spec.orgNameUpper, PAGE_W / 2, topY, {
        size: 11, align: 'center', tracking: 1.5, bold: true, colour: COLOUR_INK,
      });
    }
  } else {
    drawTracked(doc, spec.orgNameUpper, PAGE_W / 2, topY, {
      size: 11, align: 'center', tracking: 1.5, bold: true, colour: COLOUR_INK,
    });
  }

  // Big bold doc title
  const titleY = 100;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.setTextColor(COLOUR_INK);
  doc.text(spec.docTitle, PAGE_W / 2, titleY, { align: 'center' });

  // Tracked subtitle
  drawTracked(doc, spec.subtitle, PAGE_W / 2, titleY + 10, {
    size: 8.5, align: 'center', tracking: 1.4, colour: COLOUR_INK_SOFT,
  });

  // Purpose paragraph — centred, constrained width for a magazine feel
  if (spec.purpose && spec.purpose.trim()) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10.5);
    doc.setTextColor(COLOUR_INK_SOFT);
    doc.setCharSpace(0);
    const lines = wrapLines(doc, spec.purpose.trim(), 140);
    let py = titleY + 26;
    for (const l of lines) {
      doc.text(l, PAGE_W / 2, py, { align: 'center' });
      py += 5;
    }
  }

  // Project block — hairline rule + three-column bottom bar
  const blockY = 250;
  doc.setDrawColor(COLOUR_BORDER);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, blockY - 8, PAGE_W - MARGIN, blockY - 8);

  // Project name gets the full line (long names used to collide with
  // the version / month). Shrink tracking/size if it's very long.
  const pn = spec.projectNameUpper;
  const pnSize = pn.length > 40 ? 9.5 : 11.5;
  drawTracked(doc, pn, MARGIN, blockY, {
    size: pnSize, tracking: pn.length > 40 ? 0.6 : 1.2, bold: true, colour: COLOUR_INK,
  });

  // A quieter second line under the project block — issue date on
  // the left, doc reference on the right. Same tone as the footer on
  // subsequent pages so it feels continuous.
  drawTracked(doc, `ISSUED ${spec.issueDate.toUpperCase()}`, MARGIN, blockY + 6, {
    size: 7.5, tracking: 0.8, colour: COLOUR_INK_SOFT,
  });
  drawTracked(doc, `${spec.version}  ·  ${spec.issueMonthUpper}`, PAGE_W - MARGIN, blockY + 6, {
    size: 7.5, tracking: 0.8, align: 'right', colour: COLOUR_INK_SOFT,
  });
}

/**
 * Running header at the top of every non-cover page. Left:
 * `[ORG] · [DOC TYPE]`. Right: `CONFIDENTIAL · [PROJECT] · [MONTH]`.
 * Single tracked-small-caps line with a hairline rule under it.
 * Returns the y-position of the content area below the header.
 */
function drawHouseRunningHeader(doc: jsPDF, spec: HouseStyleSpec): number {
  const left = `${spec.orgNameUpper}  ·  ${spec.docTypeUpper}`;
  const rightParts: string[] = [];
  rightParts.push(spec.issueMonthUpper);
  const right = rightParts.join('  ·  ');

  drawTracked(doc, left, MARGIN, MARGIN, {
    size: 7.5, tracking: 0.7, colour: COLOUR_INK_SOFT,
  });
  drawTracked(doc, right, PAGE_W - MARGIN, MARGIN, {
    size: 7.5, tracking: 0.7, align: 'right', colour: COLOUR_INK_SOFT,
  });

  doc.setDrawColor(COLOUR_BORDER);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, MARGIN + 3, PAGE_W - MARGIN, MARGIN + 3);

  return MARGIN + 12;
}

/**
 * Numbered section header — big bold `N. Title` with an optional
 * intro paragraph below. The intro is what the Quarters reference
 * uses to explain what the section covers before the body content.
 * Falls back to no-intro when the caller passes undefined.
 * Returns the y-position after the header block.
 */
function drawHouseSection(
  doc: jsPDF,
  y: number,
  num: number,
  title: string,
  intro: string | undefined,
): number {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(COLOUR_INK);
  doc.setCharSpace(0);
  doc.text(`${num}. ${title}`, MARGIN, y);
  y += 8;

  if (intro && intro.trim()) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(COLOUR_INK_SOFT);
    const lines = wrapLines(doc, intro.trim(), CONTENT_W);
    doc.text(lines, MARGIN, y);
    y += lines.length * 4.4 + 3;
  }
  return y + 1;
}

/**
 * Sub-section heading — tracked small caps (e.g. `1.1  PROJECT
 * PARTICULARS`). Quieter than the section header, still clearly a
 * heading. Used for logical sub-groupings within a section.
 */
function drawHouseSubSection(
  doc: jsPDF,
  y: number,
  num: string,
  title: string,
): number {
  drawTracked(doc, `${num}  ${title.toUpperCase()}`, MARGIN, y, {
    size: 9, tracking: 1.3, bold: true, colour: COLOUR_INK,
  });
  // Very-thin rule under the heading — same as the reference.
  doc.setDrawColor(COLOUR_BORDER);
  doc.setLineWidth(0.15);
  doc.line(MARGIN, y + 1.5, PAGE_W - MARGIN, y + 1.5);
  return y + 6;
}

interface Revision {
  rev: string;          // e.g. '1.0'
  date: string;         // e.g. '10 Jul 2026'
  preparedBy: string;
  approvedBy: string;
  summary: string;      // wraps to multi-line
}

/**
 * Document Control / Revisions block — a numbered section
 * introduction paragraph, then the standard 5-column revision
 * table used by every long-form doc in the reference.
 */
function drawHouseRevisionSection(
  doc: jsPDF,
  y: number,
  intro: string,
  revisions: Revision[],
): number {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(COLOUR_INK);
  doc.setCharSpace(0);
  doc.text('Document Control and Revisions', MARGIN, y);
  y += 8;

  if (intro && intro.trim()) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(COLOUR_INK_SOFT);
    const lines = wrapLines(doc, intro.trim(), CONTENT_W);
    doc.text(lines, MARGIN, y);
    y += lines.length * 4.4 + 5;
  }

  // Column widths: REV | DATE | PREPARED | APPROVED | SUMMARY
  const colWs = [14, 36, 30, 30, CONTENT_W - 14 - 36 - 30 - 30];
  const cx = [
    MARGIN,
    MARGIN + colWs[0],
    MARGIN + colWs[0] + colWs[1],
    MARGIN + colWs[0] + colWs[1] + colWs[2],
    MARGIN + colWs[0] + colWs[1] + colWs[2] + colWs[3],
  ];

  // Header row — light fill, tracked caps
  const headerH = 7.5;
  doc.setFillColor('#F4F6F7');
  doc.rect(MARGIN, y, CONTENT_W, headerH, 'F');
  const headers = ['REV', 'DATE', 'PREPARED', 'APPROVED', 'SUMMARY OF CHANGES'];
  for (let i = 0; i < headers.length; i++) {
    drawTracked(doc, headers[i], cx[i] + 2.5, y + headerH - 2.5, {
      size: 7.5, tracking: 0.8, bold: true, colour: COLOUR_INK,
    });
  }
  // Hairline under header
  doc.setDrawColor(COLOUR_BORDER);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, y + headerH, PAGE_W - MARGIN, y + headerH);
  y += headerH;

  // Body rows
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(COLOUR_INK);
  doc.setCharSpace(0);
  for (const r of revisions) {
    const summaryLines = wrapLines(doc, r.summary, colWs[4] - 4);
    const rowH = Math.max(9, summaryLines.length * 4 + 4);
    doc.text(r.rev, cx[0] + 2.5, y + 5.5);
    doc.text(r.date, cx[1] + 2.5, y + 5.5);
    doc.text(r.preparedBy, cx[2] + 2.5, y + 5.5);
    doc.text(r.approvedBy, cx[3] + 2.5, y + 5.5);
    doc.text(summaryLines, cx[4] + 2.5, y + 5.5);
    // Hairline separator
    doc.setDrawColor(COLOUR_BORDER);
    doc.setLineWidth(0.15);
    doc.line(MARGIN, y + rowH, PAGE_W - MARGIN, y + rowH);
    y += rowH;
  }
  return y + 6;
}

/**
 * Two-pass page numbering. Call this AFTER every content page has
 * been drawn — walks pages 2..N and stamps `Page X of Y` on the
 * right + the issue date / doc reference on the left. Optionally
 * paints the Safe and Sorted brand attribution on the last page
 * only. Fixes task #152 (the "Page 1 of 1" bug that made every
 * page of a multi-page doc look like a truncated single-pager).
 */
function stampHousePageNumbers(
  doc: jsPDF,
  spec: HouseStyleSpec,
  opts: { skipCover?: boolean; brandAttribution?: boolean } = {},
): void {
  const total = doc.getNumberOfPages();
  const startPage = opts.skipCover ? 2 : 1;
  for (let i = startPage; i <= total; i++) {
    doc.setPage(i);
    drawTracked(doc, `${spec.issueDate.toUpperCase()}  ·  ${spec.docRef.toUpperCase()}`,
      MARGIN, PAGE_H - 8, {
        size: 7, tracking: 0.5, colour: COLOUR_INK_SOFT,
      });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(COLOUR_INK_SOFT);
    doc.setCharSpace(0);
    doc.text(`Page ${i} of ${total}`, PAGE_W - MARGIN, PAGE_H - 8, { align: 'right' });
  }

  if (opts.brandAttribution) {
    doc.setPage(total);
    drawTracked(doc, 'PREPARED WITH SAFE AND SORTED  ·  SAFEANDSORTED.COM',
      PAGE_W / 2, PAGE_H - 14, {
        size: 6.5, tracking: 1.2, align: 'center', colour: COLOUR_INK_SOFT,
      });
  }
}

/**
 * Purpose paragraphs for each doc-scope template. Cover-page
 * explanatory blurb — what the doc is, how it's used. Pattern
 * mirrors the Quarters CPP reference: one short paragraph on the
 * cover explaining the doc's role.
 *
 * TODO(pdf-house-style): move these onto the DocTemplate itself
 * (as an optional `purpose` field) so per-org customisation is
 * possible later.
 */
const HOUSE_STYLE_PURPOSES: Record<string, string> = {
  construction_phase_plan:
    'Prepared by the Principal Contractor and issued for the use of those engaged on the project. Reviewed and updated throughout the construction phase to reflect the work being carried out and any changes to risks or arrangements.',
  pre_construction_information:
    'Prepared by the Client and Principal Designer under CDM 2015. Sets out the known health and safety information about the site and the project so that the Principal Contractor can plan the construction phase safely.',
  subcontractor_pqq:
    'Used to assess a sub-contractor\'s competence, insurance and health and safety arrangements before they are engaged on a project.',
  site_welfare_plan:
    'Site welfare provision under Schedule 2 of CDM 2015 — toilets, hand-washing, drinking water, rest and changing facilities for the workforce.',
  site_fire_plan:
    'Site fire arrangements under the Regulatory Reform (Fire Safety) Order 2005 — alarm, detection, extinguishers, escape routes and muster point.',
  site_emergency_procedures:
    'Site-specific emergency response procedures for fire, serious injury, service strike, uncovered asbestos, near-miss and severe weather.',
  traffic_management_plan:
    'Site traffic arrangements — vehicle and pedestrian routes, banksman use, delivery controls and interaction with the public.',
  hs_policy_statement:
    'Top-level Health and Safety Policy Statement issued by the Director. Required by the Health and Safety at Work etc. Act 1974 for every business with five or more employees.',
  hs_arrangements:
    'Detailed Health and Safety Arrangements Manual — how the organisation puts the policy into practice: risk assessment, training, accident reporting, welfare and emergency response.',
  cdm_compliance_statement:
    'Statement confirming the organisation\'s competence, capability and arrangements to fulfil its duties under CDM 2015.',
  fire_safety_policy:
    'Fire Safety Policy compliant with the Regulatory Reform (Fire Safety) Order 2005 — applies to workplaces and shared sites.',
  first_aid_policy:
    'First Aid arrangements under the Health and Safety (First-Aid) Regulations 1981 — provision, training and reporting.',
  driving_for_work_policy:
    'Driving for Work Policy — licence checks, vehicle condition, fatigue, mobile phones and journey planning.',
  drugs_alcohol_policy:
    'Drugs and Alcohol Policy — zero tolerance for impairment on safety-critical construction work.',
  lone_working_policy:
    'Lone Working Policy — risk assessment, communication and escalation for anyone working alone on or off site.',
  gdpr_data_protection:
    'Data Protection Policy summary under UK GDPR and the Data Protection Act 2018 — lawful basis, retention, subject rights and breach response.',
  whistleblowing_statement:
    'Confidential whistleblowing channel — protection under the Public Interest Disclosure Act 1998.',
  equality_diversity_policy:
    'Equality, diversity and anti-discrimination policy under the Equality Act 2010.',
  anti_slavery_statement:
    'Modern Slavery Act 2015 statement — approach to preventing modern slavery in the business and supply chain.',
};

/**
 * Cover-page CDM / regulatory subtitle for each doc. Small-caps
 * tracked line under the doc title. Falls back to a generic label
 * derived from the doc category.
 */
const HOUSE_STYLE_SUBTITLES: Record<string, string> = {
  construction_phase_plan: 'CDM 2015  ·  REGULATION 12  ·  PRINCIPAL CONTRACTOR\'S PLAN',
  pre_construction_information: 'CDM 2015  ·  CLIENT AND PRINCIPAL DESIGNER DUTY',
  subcontractor_pqq: 'COMPETENCE ASSESSMENT  ·  PRE-QUALIFICATION QUESTIONNAIRE',
  site_welfare_plan: 'CDM 2015  ·  SCHEDULE 2  ·  WELFARE ARRANGEMENTS',
  site_fire_plan: 'FIRE SAFETY ORDER 2005  ·  SITE ARRANGEMENTS',
  site_emergency_procedures: 'CDM 2015  ·  SITE-SPECIFIC EMERGENCY RESPONSE',
  traffic_management_plan: 'CDM 2015  ·  SITE TRAFFIC MANAGEMENT',
  hs_policy_statement: 'HEALTH AND SAFETY AT WORK ETC. ACT 1974  ·  DIRECTOR\'S STATEMENT',
  hs_arrangements: 'HEALTH AND SAFETY ARRANGEMENTS MANUAL',
  cdm_compliance_statement: 'CDM 2015  ·  COMPETENCE AND CAPABILITY',
  fire_safety_policy: 'REGULATORY REFORM (FIRE SAFETY) ORDER 2005',
  first_aid_policy: 'HEALTH AND SAFETY (FIRST-AID) REGULATIONS 1981',
  driving_for_work_policy: 'DRIVING FOR WORK  ·  ORGANISATIONAL POLICY',
  drugs_alcohol_policy: 'DRUGS AND ALCOHOL  ·  ORGANISATIONAL POLICY',
  lone_working_policy: 'LONE WORKING  ·  ORGANISATIONAL POLICY',
  gdpr_data_protection: 'UK GDPR  ·  DATA PROTECTION ACT 2018',
  whistleblowing_statement: 'PUBLIC INTEREST DISCLOSURE ACT 1998',
  equality_diversity_policy: 'EQUALITY ACT 2010',
  anti_slavery_statement: 'MODERN SLAVERY ACT 2015',
};

/**
 * Short section-intro strings for the CPP — the paragraph shown
 * beneath each section header explaining what the section covers.
 * Keyed on the section index (1-based) as it appears in the CPP
 * template so we don't have to modify DocSection to carry an intro.
 */
const CPP_SECTION_INTROS: Record<string, string> = {
  'description of project': 'The basic facts about the project: what is being built, where, the main phases of work and the key programme dates.',
  'project team & responsibilities': 'The duty holders appointed under CDM 2015 and the people in control of the work. Contact details are kept current throughout the construction phase.',
  'existing information & site survey': 'The pre-construction information and existing records reviewed for this site.',
  'management of the work': 'How the work is planned, coordinated, monitored and changed safely.',
  'consulting & communicating with workers': 'How workers are consulted and kept informed about health and safety (CDM 2015 reg. 14).',
  'induction, training & competence': 'How we make sure everyone on site has the skills, knowledge, training and experience for their work.',
  'sub-contractor selection & control': 'How sub-contractors are chosen, briefed and supervised.',
  'site rules': 'The site rules operatives, sub-contractors and visitors must follow.',
  'site security & protecting the public': 'How the site is secured and members of the public are protected.',
  'welfare arrangements': 'Welfare facilities provided under Schedule 2 of CDM 2015.',
  'fire & emergency procedures': 'Fire prevention, alarm, escape and emergency arrangements.',
  'first aid': 'First-aid cover and the route to the nearest A&E.',
  'accident & incident reporting': 'How accidents, near misses and dangerous occurrences are reported and investigated.',
  'traffic, deliveries & storage': 'Vehicle and pedestrian routes, deliveries and safe storage of materials.',
  'existing services': 'Controls for work near underground and overhead services.',
  'working at height': 'Arrangements for scaffolds, towers, MEWPs, edge protection, fragile surfaces and rescue.',
  'excavations': 'Controls to prevent collapse, falls and vehicles entering excavations.',
  'temporary works': 'How temporary works are designed, checked and controlled.',
  'demolition & structural stability': 'Controls for demolition and any work affecting the stability of structures.',
  'confined spaces': 'Controls for any work in confined spaces.',
  'plant & lifting': 'Plant operation, inspection and lifting operations.',
  'hazardous substances': 'COSHH controls on this site — register, storage, PPE, spill response and disposal.',
  'asbestos': 'Asbestos information for the site and the action to take if asbestos is found.',
  'dust & silica': 'Controls for construction dust, including respirable crystalline silica.',
  'noise & vibration': 'Controls for noise and hand-arm vibration.',
  'manual handling': 'Controls to reduce manual handling risks.',
  'contaminated land': 'Controls for any contaminated ground.',
  'waste management': 'How waste is stored, removed and disposed of.',
  'health & safety file': 'Principal Contractor contributions to the Health and Safety File.',
};

/**
 * Compose a HouseStyleSpec from the caller's inputs. Handles
 * default confidentiality flag, doc-ref derivation, month/date
 * uppercasing so every caller doesn't have to re-implement it.
 */
function makeHouseSpec(input: {
  orgName: string;
  logoDataUrl: string | null;
  templateId: string;
  docTitle: string;
  docTypeUpper: string;
  subtitleOverride?: string;
  purposeOverride?: string;
  projectName: string;
  version?: string;
  issueDate: string;              // '10 July 2026'
  docRef?: string;
  confidential?: boolean;
}): HouseStyleSpec {
  const monthUpper = (() => {
    // issueDate can be either 'DD Month YYYY' or ISO — try to
    // extract 'MONTH YYYY' regardless.
    try {
      const parsed = new Date(input.issueDate);
      if (!isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }).toUpperCase();
      }
    } catch { /* noop */ }
    const parts = input.issueDate.trim().split(/\s+/);
    if (parts.length >= 3) return `${parts[parts.length - 2]} ${parts[parts.length - 1]}`.toUpperCase();
    return input.issueDate.toUpperCase();
  })();

  return {
    orgName: input.orgName,
    orgNameUpper: input.orgName.toUpperCase(),
    logoDataUrl: input.logoDataUrl,
    docTitle: input.docTitle,
    docTypeUpper: input.docTypeUpper,
    subtitle: input.subtitleOverride ?? HOUSE_STYLE_SUBTITLES[input.templateId] ?? input.docTypeUpper,
    purpose: input.purposeOverride ?? HOUSE_STYLE_PURPOSES[input.templateId] ?? '',
    projectName: input.projectName,
    projectNameUpper: input.projectName.toUpperCase(),
    version: input.version ?? 'VERSION 1.0',
    issueMonthUpper: monthUpper,
    issueDate: input.issueDate,
    docRef: input.docRef ?? `${input.docTypeUpper}  ·  ${input.projectName.toUpperCase()}`,
    confidential: input.confidential ?? true,
  };
}

/**
 * Standard house-style bullet — dot + text, wrapping to full
 * width. Consistent bullet character + spacing so lists across
 * every doc read the same.
 */
function drawHouseBullet(doc: jsPDF, y: number, text: string): number {
  const bulletX = MARGIN + 2;
  const textX = MARGIN + 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(COLOUR_INK);
  doc.setCharSpace(0);
  const lines = wrapLines(doc, text, CONTENT_W - 8);
  // Small filled dot instead of the default bullet glyph — same as
  // the reference and reads cleaner at 10pt.
  doc.setFillColor(COLOUR_INK);
  doc.circle(bulletX, y - 1.5, 0.6, 'F');
  doc.text(lines, textX, y);
  return y + lines.length * 4.5 + 1.5;
}

/**
 * Empty-section placeholder — used when a section body is blank
 * so the doc doesn't read as scruffy or truncated.
 */
function drawHouseEmptyPlaceholder(doc: jsPDF, y: number): number {
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9.5);
  doc.setTextColor(COLOUR_INK_SOFT);
  doc.setCharSpace(0);
  doc.text('This section is not populated for this project.', MARGIN, y);
  return y + 6;
}

// ============================================================
// CONSTRUCTION PHASE PLAN — bespoke renderer
// ============================================================
// CDM 2015 reg. 12 / 15. The big one — house-style layout:
//   * Cover: masthead + title + CDM subtitle + purpose + project block
//   * Page 2: Document control and revisions
//   * Page 3+: Project details table then numbered sections with intros
//   * Running header + two-pass "Page X of Y" footer on every page
//   * Sign-off page
// ============================================================

interface CppPdfOpts {
  orgName: string;
  orgLogoPath: string | null;
  fields: Array<{ key: string; label: string; value: string }>;
  sections: Array<{ heading: string; body: string }>;
  reviewChecks?: Array<{ label: string; checked: boolean }>;
  signatureDataUrl: string | null;
  signatoryName: string;
  date: string;
  notes?: string | null;
}

export async function generateCppPdf(opts: CppPdfOpts): Promise<Blob> {
  const logoDataUrl = opts.orgLogoPath
    ? await fetchSignedDataUrl('org-logos', opts.orgLogoPath)
    : null;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  function fieldVal(key: string): string {
    const f = opts.fields.find((x) => x.key === key);
    const v = f?.value || '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      return new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    }
    return v || '—';
  }

  const projectName = fieldVal('project_name');
  const spec = makeHouseSpec({
    orgName: opts.orgName,
    logoDataUrl,
    templateId: 'construction_phase_plan',
    docTitle: 'Construction Phase Plan',
    docTypeUpper: 'CONSTRUCTION PHASE PLAN',
    projectName,
    version: 'VERSION 1.0',
    issueDate: opts.date,
    docRef: `CPP  ·  ${projectName.toUpperCase()}  ·  V1.0`,
  });

  // ===== Cover page =====
  drawHouseStyleCover(doc, spec);

  // ===== Page 2 — Document Control and Revisions =====
  doc.addPage();
  let y = drawHouseRunningHeader(doc, spec);

  // TODO(pdf-house-style): once doc-supersede history is wired
  // through, seed this array from the DB (rev per supersede
  // record). For now we ship a single "First issue" row so the
  // table renders correctly on every fresh CPP.
  const revisions: Revision[] = [
    {
      rev: '1.0',
      date: opts.date,
      preparedBy: opts.signatoryName || '—',
      approvedBy: opts.signatoryName || '—',
      summary: 'First issue.',
    },
  ];
  y = drawHouseRevisionSection(
    doc,
    y,
    `This Construction Phase Plan (CPP) has been prepared by the Principal Contractor in accordance with Regulation 12 of the Construction (Design and Management) Regulations 2015 (CDM 2015). It must be reviewed and updated throughout the construction phase to reflect the work being carried out and any changes to risks or arrangements.`,
    revisions,
  );

  // ===== Project details block (styled as sub-section under an
  // implicit "Project Overview" heading) =====
  y += 4;
  y = drawHouseSubSection(doc, y, '0.1', 'Project details');
  const detailRows: Array<[string, string]> = [
    ['Project',                fieldVal('project_name')],
    ['Site address',           fieldVal('site_address')],
    ['Client',                 fieldVal('client')],
    ['Principal Contractor',   fieldVal('pc_name')],
    ['Principal Designer',     fieldVal('pd_name')],
    ['Site manager',           fieldVal('site_manager')],
    ['Construction start',     fieldVal('start_date')],
    ['Anticipated completion', fieldVal('completion_date')],
    ['F10 notification ref',   fieldVal('f10_reference')],
  ];
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(COLOUR_INK);
  doc.setCharSpace(0);
  for (const [label, value] of detailRows) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLOUR_INK_SOFT);
    doc.text(label, MARGIN, y + 4);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLOUR_INK);
    doc.text(value, MARGIN + 60, y + 4);
    doc.setDrawColor(COLOUR_BORDER);
    doc.setLineWidth(0.15);
    doc.line(MARGIN, y + 6.5, PAGE_W - MARGIN, y + 6.5);
    y += 7.5;
  }

  // ===== Page 3+ — Sections =====
  doc.addPage();
  y = drawHouseRunningHeader(doc, spec);

  function ensureSpace(needed: number): void {
    if (y + needed > PAGE_H - 20) {
      doc.addPage();
      y = drawHouseRunningHeader(doc, spec);
    }
  }

  function paragraph(text: string): void {
    if (!text || !text.trim()) return;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(COLOUR_INK);
    doc.setCharSpace(0);
    const lines = wrapLines(doc, text, CONTENT_W);
    ensureSpace(lines.length * 4.5 + 2);
    doc.text(lines, MARGIN, y);
    y += lines.length * 4.5 + 3;
  }

  for (let i = 0; i < opts.sections.length; i++) {
    const sec = opts.sections[i];
    const num = i + 1;
    const cleanHeading = sec.heading.replace(/^\d+\.\s*/, '');
    const isNa = (sec.body ?? '').trim() === 'Not applicable to this project.';
    const intro = isNa ? undefined : CPP_SECTION_INTROS[cleanHeading.toLowerCase()];

    ensureSpace(30);
    y = drawHouseSection(doc, y, num, cleanHeading, intro);

    const body = (sec.body ?? '').trim();
    if (!body) {
      y = drawHouseEmptyPlaceholder(doc, y);
    } else {
      // Multi-paragraph bodies: split on blank lines, render each
      // as its own paragraph so the reader gets breathing room.
      const paragraphs = body.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
      if (paragraphs.length > 1) {
        for (const p of paragraphs) paragraph(p);
      } else {
        // Bullet lines (start with "• " or "- ") get bullet
        // rendering; anything else is treated as plain paragraph.
        const bulletLines = body.split(/\n/).map((l) => l.trim()).filter(Boolean);
        const looksLikeBullets = bulletLines.length > 1
          && bulletLines.every((l) => /^([•·\-*]\s+|\d+\.\s+)/.test(l));
        if (looksLikeBullets) {
          for (const l of bulletLines) {
            const text = l.replace(/^([•·\-*]\s+|\d+\.\s+)/, '');
            ensureSpace(6);
            y = drawHouseBullet(doc, y, text);
          }
          y += 1;
        } else {
          paragraph(body);
        }
      }
    }
    y += 4;
  }

  if (opts.notes && opts.notes.trim()) {
    ensureSpace(20);
    y = drawHouseSection(doc, y, opts.sections.length + 1, 'Additional notes', undefined);
    paragraph(opts.notes.trim());
  }

  // ===== Sign-off page =====
  doc.addPage();
  y = drawHouseRunningHeader(doc, spec);

  y = drawHouseSection(
    doc,
    y,
    opts.sections.length + (opts.notes && opts.notes.trim() ? 2 : 1),
    'Issue and sign-off',
    'This Construction Phase Plan has been prepared by the Principal Contractor in accordance with CDM 2015. It has been reviewed against the pre-issue checklist below and is issued for use on site.',
  );

  if (opts.reviewChecks && opts.reviewChecks.length > 0) {
    y = drawHouseSubSection(doc, y + 2, 'A', 'Pre-issue review');
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLOUR_INK_SOFT);
    doc.setCharSpace(0);
    doc.text('The following items were verified before this Plan was issued:', MARGIN, y);
    y += 6;

    for (const item of opts.reviewChecks) {
      ensureSpace(8);
      drawCheckbox(doc, MARGIN, y + 1, item.checked, 4.5);
      const lines = wrapLines(doc, item.label, CONTENT_W - 10);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(COLOUR_INK);
      doc.text(lines, MARGIN + 8, y);
      y += lines.length * 4.5 + 2;
    }
    y += 5;
  }

  ensureSpace(70);
  y = drawHouseSubSection(doc, y, 'B', 'Issued by');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(COLOUR_INK_SOFT);
  doc.setCharSpace(0);
  doc.text(
    'I confirm this Construction Phase Plan has been prepared in accordance with CDM 2015 and is suitable for the project before construction works commence on site. The Plan will be reviewed and updated as the project evolves.',
    MARGIN, y, { maxWidth: CONTENT_W },
  );
  y += 16;

  doc.setDrawColor(COLOUR_BORDER);
  doc.setLineWidth(0.3);
  const sigBoxX = MARGIN;
  const sigBoxW = (CONTENT_W / 2) + 10;
  doc.roundedRect(sigBoxX, y, sigBoxW, 28, 1.2, 1.2, 'S');
  if (opts.signatureDataUrl) {
    try { doc.addImage(opts.signatureDataUrl, 'AUTO', sigBoxX + 2, y + 1, sigBoxW - 4, 18); } catch { /* noop */ }
  }
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLOUR_INK);
  doc.text(opts.signatoryName || 'Principal Contractor representative', sigBoxX + 2, y + 23);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(COLOUR_INK_SOFT);
  doc.text(opts.date, sigBoxX + sigBoxW - 2, y + 23, { align: 'right' });
  y += 32;
  drawTracked(doc, 'SIGNATURE  ·  NAME  ·  DATE', sigBoxX, y, {
    size: 7, tracking: 0.6, colour: COLOUR_INK_SOFT,
  });
  y += 10;


  // Two-pass stamp of Page X of Y + brand attribution on last page.
  stampHousePageNumbers(doc, spec, { skipCover: true, brandAttribution: true });
  return doc.output('blob');
}
